// src/purchases/index.js — екран „Доставки" (ПРОМТ-9): покупни документи с
// оригинал (PDF/снимка), ръчно въвеждане, потвърждаване в склада, справки.
//
// Правилата са на сървъра (routes/purchases.js, services/purchaseChecks.js,
// services/files.js): проверката на файла по байтове, сборовете, ЕИК,
// дубликатите, складът. Тук е само удобството — филтър по размер/брой преди
// качване и живата сверка на сборовете, която огледално повтаря сървърната.
// Екранът се вижда само при фирма с модул „Доставки" (viewAllowed в shell.js).

import { el, clear, $, field, input, row, clickableRow } from '../shared/dom.js';
import { dateInput, isoToBg, guardDates } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { loadError, emptyState, searchInput } from '../shared/ui.js';
import { bulkManager } from '../shared/pickers.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { state, cq, activeCompanyObj } from '../shared/state.js';
import { money, amount } from '../shared/format.js';
import { uploadRaw, dropZone, CLIENT_DONE_TEXT } from '../shared/upload.js';
import { render, canvasOk } from './pdfview.js';
import { openFiles } from '../files/index.js';
import { combobox } from '../shared/combobox.js';
import { categoryCombo } from '../shared/categoryCombo.js';
import { kontoExportDialog, monthSelect, recordsPreview, recordsResultDialog, exportRunsDialog, WHERE } from '../shared/kontoExport.js';
import { kontoSettingsDialog } from '../shared/kontoSettings.js';
import { MONTHS_BG } from '../shared/format.js';
import { whenFmt } from '../shared/audit.js';
import { xlsxDownload } from '../shared/xlsx.js';
import { allocate } from '../../services/costAllocation.js';
import * as pp from '../../services/purchasePeriod.js';
import { parsePack, needsPack, isMetric } from '../../services/packs.js';

const isStaff = () => { const u = state().user; return !!u && u.role !== 'client'; };

export const MAX_BYTES = 20 * 1024 * 1024;
// До 100 файла наведнъж; администраторът — без ограничение (Макс, 28.10.2026).
export const MAX_FILES = 100;
export const ACCEPT = 'application/pdf,image/jpeg,image/png,image/tiff';
const EXT_OK = /\.(pdf|jpe?g|png|tiff?)$/i;

const STATUS = { uploaded: ['Качен', 'amber'], ocr_review: ['За преглед', 'amber'], confirmed: ['Потвърден', 'green'] };
const DOC_TYPES = [['invoice', 'Фактура'], ['credit', 'Кредитно известие'], ['debit', 'Дебитно известие'], ['protocol', 'Протокол (ВОП)'], ['customs', 'Митническа декларация']];
const TREATMENTS = [['domestic', 'Вътрешна доставка'], ['eu_b2b', 'ВОП от фирма в ЕС'], ['reverse_charge', 'Обратно начисляване'], ['exempt', 'Освободена доставка'], ['eu_b2c_oss', 'OSS'], ['eu_b2c_domestic', 'Физ. лице в ЕС']];
const VAT_CREDITS = [['full', 'Пълен'], ['partial', 'Частичен (с процент)'], ['none', 'Без право на кредит']];
const VAT_RATES = [20, 9, 0];
// Най-честите разходни сметки (сметкоплан) — подсказка в „Сметка за разход";
// може да се напише и друга.
const ACCOUNTS_ID = 'pur-accounts';
const ACCOUNTS = [['601', 'Разходи за материали'], ['602', 'Разходи за външни услуги'], ['603', 'Разходи за амортизации'], ['604', 'Разходи за заплати'], ['609', 'Други разходи'], ['302', 'Материали'], ['304', 'Стоки'], ['204', 'Компютри и техника (ДМА)']];
const accountsList = () => el('datalist', { id: ACCOUNTS_ID }, ...ACCOUNTS.map(([v, t]) => el('option', { value: v, label: v + ' — ' + t })));

// period: работният месец (ГГГГ-ММ; '' = всички). null = още не е избран —
// тогава се взема първият отворен месец на фирмата (services/purchasePeriod.js).
let _f = { period: null, company: null, status: '', contact: '', category: '', noFile: false };
let _cats = null; // категориите на разходите за филтъра (само кантората)
let _periods = null; // { closed_through, first_open, today } от сървъра
let _contacts = null; // доставчиците за търсачката във филтъра
// Търсене в показания списък (№, доставчик, бележка) — на екрана, без нова
// заявка: филтрите горе стесняват на сървъра, това е „намери ми я".
let _q = '';
let _rows = [];

const isAdmin = () => !!(state().user && state().user.role === 'admin');
const isClient = () => !!(state().user && state().user.role === 'client');
const num = (v) => { const n = parseFloat(String(v == null ? '' : v).replace(',', '.')); return Number.isFinite(n) ? n : 0; };
const r2 = (n) => { const v = Number(n) || 0; const r = Math.round((Math.abs(v) + Number.EPSILON) * 100) / 100; return v < 0 ? -r : r; };
const f2 = (n) => r2(n).toFixed(2);
// Запетая в полетата за въвеждане (ПРОМТ-19, т. 5): „12,50" като навсякъде
// другаде. Без разделител за хилядите — num() не го разбира, а стойността
// на полето се чете обратно при всяко пипане. Към сървъра отива num(…).
const commaIn = (v) => String(v).replace('.', ',');
const f2in = (n) => commaIn(f2(n));
const params = (o) => Object.keys(o).filter((k) => o[k] !== '' && o[k] != null && o[k] !== false).map((k) => '&' + k + '=' + encodeURIComponent(o[k])).join('');

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  const progress = el('ul', { class: 'pur-progress', 'aria-live': 'polite' });
  const listArea = el('div');
  const ocrBar = el('div', { class: 'pur-ocr-bar' });
  page.appendChild(uploadZone(progress, () => loadList(listArea)));
  page.appendChild(ocrBar);
  page.appendChild(progress);
  // listArea НАРОЧНО остава последен: refreshList() го намира като
  // page.lastElementChild.
  page.appendChild(listArea);
  const [st] = await Promise.all([ocrStatus(state().activeCompany), loadList(listArea)]);
  // „Въведи документа ръчно" е работа на кантората — клиентът само качва (Д6).
  if (st && st.enabled === false && !isClient()) ocrBar.appendChild(ocrOffBanner());
}

// ---- OCR (ПРОМТ-10б) ----

const OCR_OFF_TEXT = 'OCR не е настроен — въведи документа ръчно';
const ocrOffBanner = () => el('div', { class: 'sev-warn', role: 'status', text: OCR_OFF_TEXT });

// Състоянието на OCR се пита веднъж на фирма: то е настройка на сървъра и
// не се сменя, докато човек работи. Неуспешна заявка не е „изключен" —
// тогава просто не казваме нищо, вместо да плашим с лента.
const _ocrStatus = new Map();
function ocrStatus(companyId) {
  if (!companyId) return Promise.resolve(null);
  if (!_ocrStatus.has(companyId)) {
    _ocrStatus.set(companyId, api('/purchases/ocr-status' + cq())
      .then((r) => (r && typeof r === 'object' && !Array.isArray(r) ? r : null))
      .catch(() => { _ocrStatus.delete(companyId); return null; }));
  }
  return _ocrStatus.get(companyId);
}

// Имената на полетата за текста „защо е жълто" — какво чува четецът.
const REVIEW_NAMES = { description: 'описанието', article: 'артикула', quantity: 'количеството', unit: 'мярката', unit_price: 'цената', subtotal: 'стойността', category: 'категорията' };
// Цената се показва с до 4 знака (1.8067): от стойността на реда тя излиза
// с повече (5.42 / 3 = 1.806666…), а в данните остава точна — сборът е от
// стойността на реда, не от закръглената цена.
const fPrice = (n) => commaIn(Math.round((Number(n) || 0) * 10000) / 10000);
let _reviewSeq = 0;

// ---- Качване ----

function uploadZone(progress, after) {
  // Зоната е обща с „Заплати" (dropZone в src/shared/upload.js).
  return dropZone({
    id: 'pur-file-input', accept: ACCEPT, multiple: true,
    title: 'Пусни PDF или снимка тук', ariaLabel: 'Качване на оригинали',
    hint: 'PDF, JPG, PNG или TIFF · до 20 MB на файл · до 100 файла наведнъж (администраторът — без ограничение). Снимката от телефона става PDF автоматично.',
    onFiles: (files) => handleFiles(files, progress, after),
  });
}

// Проверката тук е удобство (без заявка за очевидно неподходящ файл) —
// истинската е на сървъра, по байтовете.
export async function handleFiles(fileList, progress, after) {
  const files = [...(fileList || [])];
  if (!files.length) return;
  if (!isAdmin() && files.length > MAX_FILES) { toast('Най-много ' + MAX_FILES + ' файла наведнъж — избрахте ' + files.length + '. Качете ги на части.', true); return; }
  const big = files.filter((f) => f.size > MAX_BYTES);
  if (big.length) { toast('Над 20 MB: ' + big.map((f) => f.name).join(', ') + '. Намалете файла (напр. сканирайте на 200–300 dpi) и опитайте пак.', true); return; }
  const bad = files.filter((f) => !EXT_OK.test(f.name));
  if (bad.length) { toast('Не е PDF или снимка: ' + bad.map((f) => f.name).join(', ') + '.', true); return; }
  for (const f of files) {
    const li = el('li', { class: 'pur-up busy' }, el('span', { class: 'pur-up-name', text: f.name }), el('span', { class: 'pur-up-state', text: 'качва се…' }));
    if (progress) progress.appendChild(li);
    const st = li.querySelector('.pur-up-state');
    try {
      const r = await uploadOne(f);
      li.classList.remove('busy');
      if (r.duplicate) {
        li.classList.add('dup');
        st.textContent = 'вече е качен, виж документ № ' + (r.doc.number || r.doc.id) + ' ';
        st.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Отвори', 'aria-label': 'Отвори документа на ' + f.name, onClick: () => openDoc(r.doc.id) }));
      } else {
        li.classList.add('ok');
        // Клиентът не следи разчитането — то е за кантората (Д6).
        if (isClient()) st.textContent = CLIENT_DONE_TEXT;
        else { st.textContent = 'качен ✓'; trackOcr(li, st, r.doc); }
      }
    } catch (e) {
      li.classList.remove('busy');
      li.classList.add('err');
      st.textContent = e.message;
    }
  }
  if (after) await after();
}

// След качването редът под зоната следи и разчитането: „чете се…" →
// „прочетен ✓ · за преглед · месец 08.2026" с „Отвори". Списъкът е по работен
// месец, а току-що качената фактура още няма дата и не е в показания месец —
// без това човек не виждаше, че е прочетена, докато не опресни (Макс, 27.10.2026).
let _uploadPollMs = 15000;
export function _setUploadPoll(ms) { _uploadPollMs = ms; }
function trackOcr(li, st, doc) {
  if (!doc || !doc.id || !pending(doc)) return;
  li.classList.add('busy');
  st.textContent = 'качен ✓ · чете се…';
  const tick = async () => {
    if (!li.isConnected) return;
    let fresh;
    try { fresh = await api('/purchases/' + doc.id); } catch (e) { fresh = null; }
    if (!li.isConnected) return;
    if (!fresh || pending(fresh)) { setTimeout(tick, _uploadPollMs); return; }
    li.classList.remove('busy');
    const err = fresh.ocr && fresh.ocr.error;
    st.textContent = err ? 'качен ✓ · не се прочете: ' + err + ' '
      : 'прочетен ✓ · ' + String((STATUS[fresh.status] || [fresh.status || ''])[0]).toLowerCase()
        + (pp.isMonth(fresh.period) ? ' · месец ' + pp.bg(fresh.period) : '') + ' ';
    if (!err && fresh.probable_duplicate) { li.classList.add('dup'); st.textContent += '· ' + dupText(fresh.probable_duplicate) + ' '; }
    st.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Отвори', 'aria-label': 'Отвори документа на ' + (fresh.file_name || 'файла'), onClick: () => openDoc(fresh.id) }));
    refreshList();
  };
  setTimeout(tick, _uploadPollMs);
}

async function uploadOne(file, docId) {
  const url = docId ? '/api/purchases/' + docId + '/attach' : '/api/purchases/upload' + cq();
  // Общият помощник (src/shared/upload.js) — същият път ползва и POS импортът.
  return uploadRaw(url, file);
}

// ---- Списък ----

// „Вероятно вече е качена като № …" — същият доставчик (по ЕИК), вид, номер
// и година (сървърът: probable_duplicate).
const dupText = (dup) => 'Вероятно вече е качена като № ' + (dup.number || dup.id) + (dup.date ? ' от ' + isoToBg(dup.date) : '') + (dup.status === 'confirmed' ? ' (потвърдена).' : ' (чернова).');

// Документ, който OCR-ът чете в момента (ocr.pending от сървъра).
const pending = (d) => !!(d && d.ocr && d.ocr.pending && d.status !== 'confirmed');
// Разчитането отнема минути — докато в списъка има такъв документ, той се
// опреснява сам, за да се види кога е станал „За преглед".
const LIST_POLL_MS = 15000;
let _listPoll = null;

async function loadList(area) {
  clearTimeout(_listPoll);
  clear(area);
  let rows;
  // Друга фирма — нов работен месец и нов списък с доставчици.
  if (_f.company !== state().activeCompany) { _f = { period: null, company: state().activeCompany, status: '', contact: '', category: '', noFile: false }; _contacts = null; _cats = null; }
  try { _periods = await api('/purchases/periods' + cq()); } catch (e) { _periods = null; }
  if (_f.period == null) _f.period = (_periods && _periods.first_open) || '';
  const q = _f.noFile ? '/purchases/without-original' + cq() : '/purchases' + cq() + params({ period: _f.period, status: _f.status, contact_id: _f.contact, category: _f.category });
  try { rows = await api(q); }
  catch (e) { area.appendChild(loadError(() => loadList(area), e.message)); return; }
  if (_contacts == null) _contacts = await api('/contacts' + cq()).catch(() => []);
  if (_cats == null) _cats = isStaff() ? await api('/purchases/categories' + cq()).then((r) => (r && r.categories) || []).catch(() => []) : [];
  _rows = Array.isArray(rows) ? rows : [];
  if (_rows.some(pending)) _listPoll = setTimeout(() => { if (area.isConnected) loadList(area); }, LIST_POLL_MS);
  const panel = el('div', { class: 'panel' });
  // Избор на много доставки (Макс, 20.10.2026) — само администраторът, както
  // масовото триене. Потвърждаването минава ЕДНА ПО ЕДНА през обичайния
  // адрес: всяка пази своите проверки (сборове, дубликат, склад), а
  // неуспелите се изброяват с причината.
  const bulk = bulkManager({ endpoint: '/purchases', refresh: () => loadList(area), label: 'доставки', actions: [
    { label: 'Разчети избраните', run: (ids) => bulkOcr(ids, () => loadList(area)) },
    { label: 'Потвърди избраните', run: (ids) => bulkConfirm(ids, () => loadList(area)) },
    { label: 'Оригиналите (ZIP)', run: (ids) => downloadZipIds(ids) },
    // оригиналите + проверените редове на потвърдените — за мерене на OCR-а (29.10.2026)
    ...(isAdmin() ? [{ label: 'ZIP за мерене на OCR', run: (ids) => downloadZipIds(ids, true) }] : []),
  ] });
  // Търсачката е за № — на фактурата на доставчика, поредния № или СС
  // (Макс, 27.10.2026); намира и по доставчик и бележка.
  const search = searchInput({ placeholder: '№ на фактура, пореден № или СС…', ariaLabel: 'Търсене на доставка по номер или доставчик', value: _q, onSearch: (q) => { _q = q; drawRows(); } });
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: _f.noFile ? 'Доставки без оригинал' : 'Доставки' }),
    ...bulk.toolbar,
    // Всички бутони на един ред (ПРОМТ-34); на тесен екран се пренасят
    // (.pur-actions — flex-wrap).
    el('div', { class: 'pur-actions' },
      isAdmin() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Файлове без документ', onClick: () => orphansDialog() }) : null,
      isStaff() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Файлове за Конто', onClick: () => exportRunsDialog({ kind: 'purchases' }) }) : null,
      isStaff() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Настройки за Конто…', onClick: () => kontoSettingsDialog(activeCompanyObj(), null, { kind: 'purchases' }) }) : null,
      isStaff() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Експорт за Конто', onClick: () => kontoPurchasesDialog(() => loadList(area)) }) : null,
      el('button', { class: 'btn sm', type: 'button', text: '+ Ръчна доставка', onClick: () => docDialog(null) }))));
  panel.appendChild(filters(area, search));
  if (!_rows.length) {
    panel.appendChild(emptyState('▥', _f.noFile ? 'Всички доставки имат оригинал.' : 'Няма доставки по този филтър. Пуснете PDF или снимка горе, или въведете доставка на ръка.'));
    area.appendChild(panel);
    return;
  }
  const tb = el('tbody');
  const shownLbl = el('div', { class: 'pur-shown', role: 'status', style: 'color:var(--muted);font-size:13px;padding:6px 4px' });
  function drawRows() {
    clear(tb);
    bulk.reset();
    const q = String(_q || '').trim().toLowerCase();
    const rowsNow = !q ? _rows : _rows.filter((d) => [d.number, d.seq_no, d.cc_no, d.contact_name, d.notes].some((v) => String(v == null ? '' : v).toLowerCase().includes(q)));
    shownLbl.textContent = rowsNow.length + ' от ' + _rows.length + (rowsNow.length ? '' : ' — нищо не е намерено за „' + _q + '".');
    for (const d of rowsNow) {
      const [label, color] = STATUS[d.status] || [d.status, ''];
      tb.appendChild(clickableRow(() => openDoc(d.id), 'Отвори доставка № ' + (d.number || d.id) + (d.contact_name ? ' от ' + d.contact_name : ''),
        bulk.cell(d.id, 'доставка № ' + (d.number || d.id)),
        el('td', { class: 'num pur-seq', text: d.seq_no ? String(d.seq_no) : '—' }),
        el('td', { class: 'pur-clip' }, d.has_file ? el('span', { title: 'Има оригинал', 'aria-label': 'Има оригинал', text: '📎' }) : el('span', { class: 'tag amber', text: 'без оригинал' })),
        el('td', {}, el('span', { class: 'tag ' + color, text: label }), d.locked ? el('span', { title: 'Заключен (Конто)', 'aria-label': 'заключен', text: ' 🔒' }) : null,
          pending(d) ? el('span', { class: 'tag pur-reading', text: 'Разчита се…' }) : null,
          d.probable_duplicate ? el('span', { class: 'tag amber pur-dup-tag', title: dupText(d.probable_duplicate), text: 'вероятно дубликат' }) : null),
        // Един ред текст: дългото се реже с „…", целият текст е при задържане.
        el('td', { class: 'pur-cut', style: 'font-weight:600', title: d.contact_name || '', text: d.contact_name || '—' }),
        el('td', { class: 'pur-cut', title: d.number || '', text: d.number || '—' }),
        el('td', {}, el('span', { text: d.date ? isoToBg(d.date) : '—' }),
          // При „Всички месеци" и когато месецът не е този на датата (закъсняла).
          d.period && (!_f.period || d.period !== pp.monthOf(d.date)) ? el('span', { class: 'pur-period-tag', title: 'Работен месец', text: ' → ' + pp.bg(d.period) }) : null),
        el('td', { class: 'num', text: d.cc_no ? String(d.cc_no) : '—' }),
        uploadCell(d),
        el('td', { class: 'num', text: money(d.total, d.currency || 'EUR') })));
    }
  }
  drawRows();
  panel.appendChild(shownLbl);
  panel.appendChild(el('table', { class: 'list pur-list' },
    el('thead', {}, el('tr', {}, bulk.headTh, el('th', { class: 'num', title: 'Пореден № на доставката във фирмата — дава се при потвърждаване', text: 'Пор. №' }), el('th', { text: 'Оригинал' }), el('th', { text: 'Статус' }), el('th', { text: 'Доставчик' }), el('th', { text: '№ на документ' }), el('th', { text: 'Дата' }), el('th', { class: 'num', title: '№ на счетоводната статия — дава се при експорт към Конто', text: 'СС' }), el('th', { title: 'Кога и кой е качил документа; кой и кога го е потвърдил (целите имена — при задържане)', text: 'Качен / потвърден' }), el('th', { class: 'num', text: 'Сума' }))),
    tb));
  area.appendChild(panel);
}

// Кой и кога е качил документа и кой го е потвърдил (Макс, 30.10.2026) —
// кратко в списъка, целият дневник е в самия документ.
// Кратко (Макс, 30.10.2026: „твърде тясно"): ден.месец час и само първото
// име; цялото — дата с година и пълните имена — при задържане и в „Дневник".
function shortWhen(iso) { const w = whenFmt(iso); return w ? w.replace(/^(\d\d\.\d\d)\.\d{4}/, '$1') : ''; }
function firstName(n) { return String(n || '').trim().split(/\s+/)[0] || ''; }
function uploadCell(d) {
  const full = 'Качен: ' + (d.uploaded_at ? whenFmt(d.uploaded_at) + (d.uploaded_by_name ? ' от ' + d.uploaded_by_name : '') : '—')
    + (d.confirmed_by_name ? '\nПотвърден: ' + (d.confirmed_at ? whenFmt(d.confirmed_at) + ' от ' : 'от ') + d.confirmed_by_name : '');
  const up = d.uploaded_at ? shortWhen(d.uploaded_at) + (d.uploaded_by_name ? ' ' + firstName(d.uploaded_by_name) : '') : '—';
  const conf = d.confirmed_by_name ? '✓ ' + (d.confirmed_at ? shortWhen(d.confirmed_at) + ' ' : '') + firstName(d.confirmed_by_name) : '';
  return el('td', { class: 'pur-log', title: full, 'aria-label': full.replace('\n', '; ') },
    el('div', { text: up }),
    conf ? el('div', { class: 'pur-log-conf', text: conf }) : null);
}

// Дневникът на документа: всичко, което е ставало с него — качване,
// разчитане, редакции (поле по поле), потвърждаване, експорт, отключване.
function historyBox(d) {
  const h = Array.isArray(d.history) ? d.history.slice().reverse() : [];
  const head = el('div', { class: 'pur-log-head' },
    el('div', {}, el('b', { text: 'Качен: ' }), d.uploaded_at ? whenFmt(d.uploaded_at) + (d.uploaded_by_name ? ' от ' + d.uploaded_by_name : '') : '—'),
    el('div', {}, el('b', { text: 'Потвърден: ' }), d.confirmed_by_name ? (d.confirmed_at ? whenFmt(d.confirmed_at) + ' от ' : 'от ') + d.confirmed_by_name : 'още не'));
  const list = el('ol', { class: 'pur-log-list' }, ...h.map((x) => el('li', {},
    el('span', { class: 'pur-log-at', text: whenFmt(x.at) }), ' ',
    el('span', { class: 'pur-log-by', text: x.by || '' }), ' — ',
    el('span', { text: x.field ? x.action + ': ' + x.field + ' „' + (x.from == null ? '' : x.from) + '" → „' + (x.to == null ? '' : x.to) + '"' : (x.action || '') }))));
  return el('details', { class: 'pur-log-box' }, el('summary', { text: 'Дневник' }), head, h.length ? list : el('p', { class: 'pur-note', text: 'Няма записи.' }));
}

// Филтрите (Макс, 27.10.2026): РАБОТЕН МЕСЕЦ вместо „от дата до дата",
// доставчикът — търсачка (стотици контакти), № — търсачка. Всичко се прилага
// веднага, без „Покажи".
function periodOptions() {
  const today = (_periods && _periods.today) || new Date().toISOString().slice(0, 10);
  const last = pp.addMonths(pp.monthOf(today), 1);
  const out = [];
  for (let m = last, i = 0; i < 25; m = pp.prevMonth(m), i++) out.push(m);
  if (_f.period && !out.includes(_f.period)) out.push(_f.period);
  return out;
}

function filters(area, search) {
  const closed = (m) => !!(_periods && _periods.closed_through && m <= _periods.closed_through);
  const period = el('select', { 'aria-label': 'Работен месец' },
    ...periodOptions().map((m) => el('option', { value: m, text: pp.bg(m) + (closed(m) ? ' — приключен' : '') })),
    el('option', { value: '', text: 'Всички месеци' }));
  period.value = _f.period || '';
  const go = () => loadList(area);
  period.addEventListener('change', () => { _f.period = period.value; go(); });
  const step = (n, label) => el('button', { class: 'btn sm ghost', type: 'button', 'aria-label': label, text: n < 0 ? '◀' : '▶', onClick: () => {
    const base = _f.period || (_periods && _periods.first_open) || pp.monthOf(new Date().toISOString());
    _f.period = pp.addMonths(base, n); go();
  } });
  const status = el('select', {}, el('option', { value: '', text: 'Всички статуси' }), ...Object.keys(STATUS).map((k) => el('option', { value: k, text: STATUS[k][0] })));
  status.value = _f.status;
  status.addEventListener('change', () => { _f.status = status.value; go(); });
  // Доставчиците най-отгоре, после другите контакти; празно поле = всички.
  const list = (Array.isArray(_contacts) ? _contacts : []).slice()
    .sort((a, b) => (a.type === 'supplier' ? 0 : 1) - (b.type === 'supplier' ? 0 : 1) || String(a.name || '').localeCompare(String(b.name || ''), 'bg'));
  if (_f.contact && !list.some((c) => String(c.id) === String(_f.contact))) {
    const d = _rows.find((x) => String(x.contact_id) === String(_f.contact));
    list.push({ id: Number(_f.contact), name: (d && d.contact_name) || 'избраният доставчик' });
  }
  const contact = combobox({
    items: list, label: (c) => c.name || '', sub: (c) => (c.eik ? 'ЕИК: ' + c.eik : ''),
    search: (c) => [c.name, c.eik, c.vat_number].filter(Boolean).join(' '),
    value: _f.contact || null, placeholder: 'Всички доставчици — пишете име или ЕИК…',
    emptyText: 'Няма такъв доставчик.',
    onPick: (c) => { const v = c ? String(c.id) : ''; if (v !== String(_f.contact || '')) { _f.contact = v; go(); } },
  });
  // Филтър по категория (ПРОМТ-46) — същата търсачка като в реда; празно = всички.
  const catFilter = Array.isArray(_cats) && _cats.length ? categoryCombo({
    categories: _cats, value: _f.category || '', placeholder: 'Всички категории — пишете…',
    onPick: (c) => { const v = c ? c.id : ''; if (v !== (_f.category || '')) { _f.category = v; go(); } },
  }) : null;
  const noFileBtn = el('button', { class: 'btn sm ghost' + (_f.noFile ? ' active' : ''), type: 'button', 'aria-pressed': _f.noFile ? 'true' : 'false', text: 'Без оригинал',
    onClick: () => { _f.noFile = !_f.noFile; go(); } });
  const zipBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'ZIP за месеца', onClick: () => {
    if (!_f.period) { toast('Изберете месец за ZIP-а.', true); return; }
    downloadZipPeriod(_f.period);
  } });
  const btns = [noFileBtn, zipBtn];
  // Приключване на месеца — когато е подаден ДДС-ът (кантората).
  // Отваря го наново само администраторът.
  const m = _f.period;
  const today = (_periods && _periods.today) || '';
  if (m && isStaff() && !closed(m) && (!today || m <= pp.monthOf(today))) {
    btns.push(el('button', { class: 'btn sm', type: 'button', text: 'Приключи ' + pp.bg(m), onClick: () => closePeriod(m, go) }));
  }
  if (m && isAdmin() && closed(m)) {
    btns.push(el('button', { class: 'btn sm ghost', type: 'button', text: 'Отвори ' + pp.bg(m), onClick: () => reopenPeriod(m, go) }));
  }
  const periodWrap = el('div', { class: 'pur-period' }, step(-1, 'Предишен месец'), period, step(1, 'Следващ месец'));
  return el('div', { class: 'pur-filters' },
    field('Работен месец', periodWrap, { size: 'm' }),
    field('Доставчик', contact, { size: 'l' }),
    field('№ на документ', search, { size: 'm' }),
    field('Статус', status, { size: 'm' }),
    catFilter ? field('Категория', catFilter, { size: 'm' }) : null,
    el('div', { class: 'pur-filter-btns' }, ...btns),
    closed(m) ? el('p', { class: 'pur-closed-note', role: 'status', text: 'Месец ' + pp.bg(m) + ' е приключен. Нова фактура от него влиза в първия отворен месец' + (_periods && _periods.first_open ? ' (' + pp.bg(_periods.first_open) + ')' : '') + '.' }) : null);
}

async function closePeriod(m, after) {
  const drafts = _rows.filter((d) => d.status !== 'confirmed').length;
  const next = pp.bg(pp.nextMonth(m));
  // Текущият месец още тече (ОТЧЕТ-ПРОМТ-38, Д7) — да се каже изрично.
  const today = (_periods && _periods.today) || '';
  const running = today && m >= pp.monthOf(today);
  const text = (running ? 'ВНИМАНИЕ: ' + pp.bg(m) + ' още не е свършил — всяка фактура с дата от него до края на месеца ще влиза в ' + next + '. ' : '')
    + 'Да приключа ли ' + pp.bg(m) + '? Правете го, когато ДДС-ът за месеца е подаден. Закъсняла фактура от него ще влиза в ' + next + '.'
    + (drafts ? ' Непотвърдените ' + drafts + ' от този месец минават в ' + next + '.' : '');
  if (!(await confirmDialog(text, { okText: 'Приключи' }))) return;
  try {
    const r = await api('/purchases/close-period', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, month: m }) });
    toast('Месец ' + pp.bg(m) + ' е приключен.' + (r.moved ? ' ' + r.moved + ' непотвърдени минаха в ' + pp.bg(r.first_open) + '.' : ''));
    _f.period = r.first_open;
    after();
  } catch (e) { toast(e.message, true); }
}

async function reopenPeriod(m, after) {
  if (!(await confirmDialog('Да отворя ли пак ' + pp.bg(m) + ' (и месеците след него)? Новите фактури от него пак ще влизат в него.', { okText: 'Отвори' }))) return;
  try {
    await api('/purchases/reopen-period', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, month: m }) });
    toast('Месец ' + pp.bg(m) + ' е отворен.');
    after();
  } catch (e) { toast(e.message, true); }
}

function downloadZipPeriod(m) {
  const a = el('a', { href: '/api/purchases/export-zip' + cq() + params({ period: m }), download: 'Доставки_' + pp.bg(m) + '.zip', style: 'display:none' });
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// „Експорт за Конто" (ПРОМТ-11; от ПРОМТ-29 — TXT за „Импорт на
// записвания": CRM-ът дава статията Дт разход / Дт 4531 / Кт 401);
// изнесените се заключват. Само за кантората — клиентът не внася нищо в Конто.
function kontoPurchasesDialog(done) {
  const month = monthSelect('Месец');
  // По подразбиране — месецът, който е отворен в списъка.
  if (_f.period && [...month.options].some((o) => o.value === _f.period)) month.value = _f.period;
  const allBox = el('input', { type: 'checkbox' });
  return kontoExportDialog({
    kind: 'purchases', title: 'Експорт за Конто — доставки', url: '/purchases/konto-export',
    intro: 'Потвърдените доставки за работния месец — като статии за „Импорт на записвания" (TXT). При свалянето всяка получава № на счетоводна статия (СС) и се заключва — поправка после само с отключване от администратор.',
    controls: [field('Месец', month, { size: 'm' }), el('label', { style: 'display:flex;gap:8px;align-items:center;margin:6px 0' }, allBox, el('span', { text: 'Включи и вече изнесените (файлът наново)' }))],
    payload: () => ({ filters: { month: month.value, include_exported: allBox.checked } }),
    sub: (r) => 'Записвания (TXT) — CRM-ът дава сметките на статията.' + (r.already_exported ? ' Вече изнесени за месеца: ' + r.already_exported : ''),
    where: () => WHERE.purchases,
    extra: (r) => {
      const prev = previousRunsNote(r, month.value, allBox.checked);
      const x = recordsPreview(r);
      return prev || x ? el('div', {}, prev, x) : null;
    },
    onDone: (r) => {
      if (done) done(r);
      if (r && r.run_id) recordsResultDialog(r, { kind: 'purchases' });
    },
  });
}

// „За октомври вече е свален файл на 28.10 от Макс (5 доставки)…" (ПРОМТ-34):
// повторният експорт не е грешка, но човек трябва да знае, че в Конто вече е
// внесено нещо за месеца и кое ще излезе сега.
export function previousRunsNote(r, monthValue, includeExported) {
  const runs = (r && Array.isArray(r.previous_runs)) ? r.previous_runs : [];
  if (!runs.length) return null;
  const last = runs[0];
  const m = /^(\d{4})-(\d{2})$/.exec(String(monthValue || ''));
  const monthName = m ? MONTHS_BG[Number(m[2]) - 1].toLowerCase() : 'този месец';
  const when = new Date(last.created_at);
  const day = Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString('bg-BG', { timeZone: 'Europe/Sofia', day: '2-digit', month: '2-digit' });
  const n = Number(last.docs) || 0;
  const text = 'За ' + monthName + ' вече е свален файл' + (day ? ' на ' + day : '') + (last.created_by_name ? ' от ' + last.created_by_name : '')
    + ' (' + n + ' ' + (n === 1 ? 'доставка' : 'доставки') + ')' + (runs.length > 1 ? ', общо ' + runs.length + ' файла за месеца' : '') + '. '
    + (includeExported
      ? 'Отметнато е „Включи и вече изнесените" — старите доставки ще са и в новия файл. Не ги внасяйте в Конто втори път.'
      : 'Новите доставки ще са в нов файл; старите не се повтарят.');
  return el('div', { class: 'alert amber pur-prev-runs', role: 'status', style: 'margin-top:8px' }, el('div', { text }),
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Файлове за Конто', style: 'margin-top:6px', onClick: () => exportRunsDialog({ kind: 'purchases' }) }));
}

export function zipUrl(from, to) {
  return '/api/purchases/export-zip' + cq() + params({ from, to });
}

// Оригиналите само на избраните (ids) — същият адрес като „ZIP за период".
function downloadZipIds(ids, truth) {
  const a = el('a', { href: '/api/purchases/export-zip' + cq() + params(truth ? { ids: ids.join(','), truth: '1' } : { ids: ids.join(',') }),
    download: truth ? 'Доставки_за_мерене_OCR.zip' : 'Доставки_избрани.zip', style: 'display:none' });
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// „Разчети избраните": наново през OCR — само непотвърдените с оригинал.
// Сървърът ги нарежда; списъкът се опреснява сам, докато се разчитат.
async function bulkOcr(ids, after) {
  const todo = ids.filter((id) => { const d = _rows.find((x) => Number(x.id) === Number(id)); return d && d.status !== 'confirmed' && !d.locked && d.has_file; });
  if (!todo.length) { toast('Няма какво да се разчете — избраните са потвърдени или без оригинал.', true); return; }
  if (!(await confirmDialog('Да разчета ли наново ' + todo.length + ' ' + (todo.length === 1 ? 'документ' : 'документа') + '? Попълненото в тях ще се замени с разчетеното. Отнема по 1–5 минути на документ.', { okText: 'Разчети' }))) return;
  try {
    const r = await api('/purchases/ocr-bulk', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, ids: todo }) });
    toast('Разчитат се: ' + r.queued.length + '. Списъкът ще се обнови сам.');
    if (r.skipped && r.skipped.length) toast('Не са наредени ' + r.skipped.length + ' — ' + r.skipped.slice(0, 5).map((x) => '№ ' + (x.number || x.id) + ': ' + x.reason).join(' · '), true);
  } catch (e) { toast(e.message, true); }
  after();
}

async function bulkConfirm(ids, after) {
  const todo = ids.filter((id) => { const d = _rows.find((x) => Number(x.id) === Number(id)); return d && d.status !== 'confirmed'; });
  if (!todo.length) { toast('Избраните вече са потвърдени.'); return; }
  if (!(await confirmDialog('Да потвърдя ли ' + todo.length + ' ' + (todo.length === 1 ? 'доставка' : 'доставки') + '? Всяка минава своите проверки; стоката влиза в склада.', { okText: 'Потвърди' }))) return;
  let ok = 0;
  const bad = [];
  for (const id of todo) {
    try { await api('/purchases/' + id + '/confirm', { method: 'POST', body: '{}' }); ok++; }
    catch (e) {
      const d = _rows.find((x) => Number(x.id) === Number(id)) || {};
      bad.push('№ ' + (d.number || id) + ': ' + e.message);
    }
  }
  toast('Потвърдени: ' + ok + '.');
  if (bad.length) toast('Не са потвърдени ' + bad.length + ' — ' + bad.slice(0, 5).join(' · ') + (bad.length > 5 ? ' …' : ''), true);
  after();
}


// ---- Документ ----

export async function openDoc(id) {
  let doc;
  try { doc = await api('/purchases/' + id); } catch (e) { toast(e.message, true); return; }
  return docDialog(doc);
}

function lineValue(l) {
  const d = Math.min(Math.max(num(l.discount), 0), 100);
  return r2(num(l.quantity) * num(l.unit_price) * (1 - d / 100));
}

// Огледало на services/purchaseChecks.js (sumsError + vatError): същите
// правила и допуски, за да не казва екранът „зелено", а сървърът „не".
// Половин стотинка на ред, поне 0,01 — като linesTol в сървъра.
const linesTol = (n) => Math.max(0.01, r2(0.005 * n));

export function checkTotals(t) {
  const lines = t.lines || [];
  const out = [];
  if (!lines.length) { out.push('Добавете поне един ред.'); return out; }
  const sum = r2(lines.reduce((s, l) => s + lineValue(l), 0));
  if (Math.abs(r2(sum - num(t.subtotal))) > linesTol(lines.length) + 1e-9) out.push('Сборът на редовете (' + amount(r2(sum)) + ') не съвпада с основата (' + amount(r2(t.subtotal)) + ').');
  if (r2(num(t.subtotal) + num(t.vat_amount)) !== r2(t.total)) out.push('Основа + ДДС (' + amount(r2(num(t.subtotal) + num(t.vat_amount))) + ') не е равно на общата сума (' + amount(r2(t.total)) + ').');
  // по ставки, като сървъра (purchaseChecks.vatError): ред с ДДС 0 % (застраховка)
  // не носи ДДС на фактура с 20 %
  const zero = r2(lines.filter((l) => l && l.vat_rate != null && l.vat_rate !== '' && Number(l.vat_rate) === 0).reduce((s, l) => s + lineValue(l), 0));
  const expected = r2(r2(sum - zero) * num(t.vat_rate) / 100);
  const tol = Math.max(0.01, r2(0.01 * lines.length));
  if (Math.abs(r2(expected - num(t.vat_amount))) > tol + 1e-9) out.push('ДДС (' + amount(r2(t.vat_amount)) + ') не отговаря на ' + num(t.vat_rate) + ' % от редовете (очаквано ' + amount(r2(expected)) + ').');
  // Окончателна, изцяло покрита от аванс (ПРОМТ-44) — 0 € е законно.
  const covered = r2(num(t.total)) === 0 && lines.some((l) => l && l.advance_of != null);
  if (t.doc_type === 'credit' ? !(num(t.total) < 0) : !(num(t.total) > 0) && !covered) out.push(t.doc_type === 'credit' ? 'Кредитното известие е с отрицателна обща сума.' : 'Общата сума трябва да е положителна.');
  return out;
}

// Основата по документа минус Σ редове, когато е в допуска, но не е 0.
// Само за сведение (сива бележка): основата и ДДС-ът остават от фактурата,
// защото дневникът на покупките се води по тях.
export function roundingDiff(t) {
  const lines = t.lines || [];
  if (!lines.length) return 0;
  const diff = r2(num(t.subtotal) - r2(lines.reduce((s, l) => s + lineValue(l), 0)));
  return diff !== 0 && Math.abs(diff) <= linesTol(lines.length) + 1e-9 ? diff : 0;
}

// Придружаващи документи към доставката (Макс, 19.10.2026): към фактурата
// често трябва и ЧМР, митническа декларация и т.н. Оригиналът остава един —
// него чете OCR-ът; другите само се пазят и показват. Списъкът е същият като
// на сървъра (ATTACH_LABELS в routes/purchases.js).
export const ATTACH_LABELS = ['Фактура за транспорт', 'ЧМР', 'Митническа декларация', 'Товарителница', 'Протокол', 'Друг документ'];

// Без страничната лента на PDF прегледа на браузъра и по ширината на рамката —
// както прегледът на фактурата в „Изпрати по имейл".
export const pdfView = (url) => url + '#navpanes=0&view=FitH';

function docsPane(d, isNew, locked) {
  const pane = el('div', { class: 'pur-docs' });
  const atts = Array.isArray(d.attachments) ? d.attachments : [];
  // Какво се вижда: оригиналът (ако го има), после придружаващите.
  const items = [];
  if (d.has_file) items.push({ key: 'main', label: 'Оригинал', url: '/api/purchases/' + d.id + '/file', name: d.file_name || 'документ' });
  atts.forEach((a) => items.push({ key: 'a' + a.file_id, label: a.label, url: '/api/purchases/' + d.id + '/attachments/' + a.file_id, name: a.file_name, att: a }));

  const tabs = el('div', { class: 'pur-doc-tabs', role: 'group', 'aria-label': 'Документи към доставката' });
  const view = el('div', { class: 'pur-doc-view' });
  let current = null;
  // Оригиналът с местата на редовете от OCR-а → рисуван тук (pdfview.js), за да
  // се огражда редът, който човекът проверява (29.10.2026). Иначе — PDF прегледът.
  const boxes = (d.lines || []).some((l) => Array.isArray(l.ocr_box));
  let viewer = null;
  let asPdf = false;
  const frame = (it) => el('iframe', { class: 'pur-frame', src: pdfView(it.url), title: it.label + ': ' + (it.name || 'документ') });
  const show = (it) => {
    current = it;
    viewer = null;
    clear(view);
    if (it.key === 'main' && boxes && !asPdf && canvasOk(view.ownerDocument)) {
      const canvasBox = el('div', { class: 'pur-canvas', role: 'img', 'aria-label': it.label + ': ' + (it.name || 'документ') + ' — редът, в който сте, е ограден' },
        el('p', { class: 'pur-note', text: 'Зарежда се…' }));
      view.appendChild(canvasBox);
      view.appendChild(el('button', { class: 'btn sm ghost pur-as-pdf', type: 'button', text: 'Отвори като PDF',
        onClick: () => { asPdf = true; show(it); } }));
      render(canvasBox, it.url, d.ocr && d.ocr.words).then((v) => {
        if (current !== it) return;
        canvasBox.querySelector('.pur-note')?.remove();
        viewer = v;
        if (pane._pending) viewer.highlight(pane._pending);
      }).catch(() => { if (current === it) { asPdf = true; show(it); } });
    } else {
      view.appendChild(frame(it));
      if (it.key === 'main' && boxes && asPdf && canvasOk(view.ownerDocument)) {
        view.appendChild(el('button', { class: 'btn sm ghost pur-as-pdf', type: 'button', text: 'Изглед с оградени редове',
          onClick: () => { asPdf = false; show(it); } }));
      }
    }
    if (it.key === 'main' && d.retention_until) view.appendChild(el('p', { class: 'pur-retention', text: 'Оригиналът се пази до ' + isoToBg(d.retention_until) + '.' }));
    if (it.att) {
      const by = [it.att.added_by_name, it.att.added_at ? isoToBg(String(it.att.added_at).slice(0, 10)) : ''].filter(Boolean).join(', ');
      const line = el('p', { class: 'pur-retention', text: (it.name || '') + (by ? ' — прикачен от ' + by : '') + '. ' });
      if (!locked) line.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Махни документа', onClick: () => removeAttachment(d, it.att) }));
      view.appendChild(line);
    }
    tabs.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', b.dataset.key === it.key ? 'true' : 'false'));
  };
  items.forEach((it) => tabs.appendChild(el('button', { class: 'btn sm ghost pur-doc-tab', type: 'button', 'data-key': it.key, 'aria-pressed': 'false', text: it.label, onClick: () => show(it) })));

  if (!d.has_file) {
    const box = el('div', { class: 'pur-nofile' }, el('p', { text: isNew ? 'Ръчно въведена доставка. Оригиналът се прикачва след записа — както и ЧМР и другите документи.' : 'Документът няма оригинал.' }));
    if (!isNew && !locked) box.appendChild(filePick('pur-attach-' + d.id, 'Прикачи оригинал', (f) => uploadOne(f, d.id), 'Оригиналът е прикачен.', d.id));
    pane.appendChild(box);
  }
  if (items.length) {
    // Един документ — без излишни бутони; два и повече — превключване.
    if (items.length > 1) pane.appendChild(tabs);
    pane.appendChild(view);
    show(items[0]);
  }
  if (!isNew) pane.appendChild(attachRow(d));
  // Огражда реда във фактурата; ако изгледът още се зарежда — след това.
  pane.highlight = (box) => {
    pane._pending = box;
    return viewer ? viewer.highlight(box) : false;
  };
  return pane;
}

// Скрито поле за файл + бутон-етикет (голото поле пише на английски).
function filePick(id, text, send, okText, docId) {
  const wrap = el('span', {});
  const fin = el('input', { type: 'file', accept: ACCEPT, class: 'sr-only', id });
  fin.addEventListener('change', async () => {
    const f = fin.files && fin.files[0];
    fin.value = '';
    if (!f) return;
    if (f.size > MAX_BYTES) { toast('Файлът е над 20 MB.', true); return; }
    try {
      await send(f);
      toast(okText);
      closeModal();
      openDoc(docId);
      refreshList();
    } catch (e) { toast(e.message, true); }
  });
  wrap.appendChild(el('label', { class: 'btn', for: id, tabindex: '0', text, onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fin.click(); } } }));
  wrap.appendChild(fin);
  return wrap;
}

// „+ Прикачи документ": вид (ЧМР…) и файл. Позволено и при потвърдена или
// изнесена доставка — ЧМР-ът често идва след фактурата и не пипа нито
// склада, нито Конто.
function attachRow(d) {
  const kind = el('select', {}, ATTACH_LABELS.map((l) => el('option', { value: l, text: l })));
  return el('div', { class: 'pur-attach-row' },
    field('Вид на документа', kind),
    filePick('pur-extra-' + d.id, '+ Прикачи документ', (f) => uploadRaw('/api/purchases/' + d.id + '/attachments?label=' + encodeURIComponent(kind.value), f), 'Документът е прикачен.', d.id));
}

async function removeAttachment(d, a) {
  const ok = await confirmDialog('Да махна „' + a.label + '" от доставката? Файлът не се трие — остава при администратора във „Файлове без документ".', { okText: 'Махни', danger: true });
  if (!ok) return;
  try {
    await api('/purchases/' + d.id + '/attachments/' + a.file_id, { method: 'DELETE' });
    toast('Документът е махнат.');
    closeModal();
    openDoc(d.id);
    refreshList();
  } catch (e) { toast(e.message, true); }
}

// ---- Разходи за разпределяне (Макс, 19.10.2026) ----
// Транспорт, мито и други разходи по документи, прикачени към СЪЩАТА
// доставка, влизат в доставната цена на стоките — пропорционално на
// стойността на реда, както в таблицата на кантората. Сметката е една за
// екрана и сървъра (services/costAllocation.js); записът е PUT /:id/costs.
const COST_KINDS = [['transport', 'Транспорт'], ['customs', 'Мито'], ['other', 'Други']];
const eur = (n) => (Number(n) || 0).toFixed(2).replace('.', ',');

function costsSection(d, arts) {
  const list = state().companies || [];
  const co = list.find((c) => Number(c.id) === Number(d.company_id)) || list.find((c) => Number(c.id) === Number(state().activeCompany)) || {};
  const mode = co.warehouse_mode;
  if (!d.id || (d.doc_type || 'invoice') !== 'invoice' || (mode !== 'trade' && mode !== 'materials')) return null;
  const box = el('div', { class: 'pur-costs' }, el('h4', { text: 'Разходи за разпределяне' }), el('p', { class: 'pur-note', text: 'Транспорт, мито и други разходи по тази доставка — влизат в цената на стоката.' }));
  const atts = Array.isArray(d.attachments) ? d.attachments : [];
  if (!atts.length) {
    box.appendChild(el('p', { class: 'pur-note', text: 'За да разпределите транспорт или мито върху стоките, първо прикачете фактурата за транспорта или митническата декларация отляво („+ Прикачи документ“).' }));
    return box;
  }
  let costs = (Array.isArray(d.costs) ? d.costs : []).map((c) => Object.assign({}, c));
  const artsById = new Map((arts || []).map((a) => [Number(a.id), a]));
  const rowsBox = el('div', {});
  const preview = el('div', { class: 'pur-costs-preview', 'aria-live': 'polite' });

  const draw = () => {
    clear(rowsBox);
    // Заглавия над колоните — полетата в реда са без собствен етикет (те
    // имат aria-label за четеца), а човек трябва да знае кое какво е.
    if (costs.length) rowsBox.appendChild(el('div', { class: 'pur-cost-row pur-cost-head', 'aria-hidden': 'true' },
      el('span', { text: 'Вид' }), el('span', { text: 'Документ' }), el('span', { text: 'Сума в евро' }), el('span')));
    costs.forEach((c, i) => {
      const n = i + 1;
      const kind = el('select', { 'aria-label': 'Вид разход, ред ' + n, onChange: (e) => { c.kind = e.target.value; drawPreview(); } },
        COST_KINDS.map(([v, t]) => el('option', { value: v, text: t, selected: c.kind === v })));
      const doc = el('select', { 'aria-label': 'Документ за разхода, ред ' + n, onChange: (e) => { c.file_id = Number(e.target.value); } },
        atts.map((a) => el('option', { value: a.file_id, text: a.label + ' — ' + (a.file_name || ''), selected: Number(c.file_id) === Number(a.file_id) })));
      if (c.file_id == null) c.file_id = atts[0].file_id;
      const amt = input({ value: c.amount != null ? String(c.amount).replace('.', ',') : '', inputmode: 'decimal', 'aria-label': 'Сума в евро, ред ' + n,
        onInput: (e) => { c.amount = num(e.target.value); drawPreview(); } });
      rowsBox.appendChild(el('div', { class: 'pur-cost-row' }, kind, doc, amt,
        el('button', { class: 'btn sm ghost', type: 'button', 'aria-label': 'Махни разход ' + n, text: '×', onClick: () => { costs.splice(i, 1); draw(); } })));
    });
    drawPreview();
  };

  const calc = () => allocate(Object.assign({}, d, { costs }), artsById, mode);
  const drawPreview = () => {
    clear(preview);
    const r = calc();
    if (!costs.length) return;
    if (!r.rows.length) { preview.appendChild(el('p', { class: 'sev-warn', text: 'Няма редове със складов артикул — разходът няма върху какво да се разпредели. Изберете артикулите на редовете и запишете доставката.' })); return; }
    const tb = el('tbody');
    r.rows.forEach((x) => tb.appendChild(el('tr', {},
      el('td', { text: x.description }), el('td', { class: 'num', text: eur(x.value) }), el('td', { class: 'num', text: String(x.share).replace('.', ',') }),
      el('td', { class: 'num', text: eur(x.transport) }), el('td', { class: 'num', text: eur(x.customs) }), el('td', { class: 'num', text: eur(x.other) }),
      el('td', { class: 'num', text: eur(x.newValue) }), el('td', { class: 'num', text: String(x.qty).replace('.', ',') + ' ' + x.unit }),
      el('td', { class: 'num', style: 'font-weight:600', text: eur(x.unitPrice) }))));
    const t = r.totals;
    tb.appendChild(el('tr', { class: 'pur-costs-total' }, el('td', { text: 'Общо' }), el('td', { class: 'num', text: eur(t.value) }), el('td', { class: 'num', text: '100' }),
      el('td', { class: 'num', text: eur(t.transport) }), el('td', { class: 'num', text: eur(t.customs) }), el('td', { class: 'num', text: eur(t.other) }),
      el('td', { class: 'num', text: eur(t.newValue) }), el('td'), el('td')));
    preview.appendChild(el('div', { class: 'pur-lines-wrap' }, el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, ...['Стока', 'Стойност €', 'Дял %', 'Транспорт', 'Мито', 'Други', 'Нова стойност €', 'Кол.', 'Нова ед. цена €'].map((h) => el('th', { scope: 'col', text: h })))),
      tb)));
    preview.appendChild(el('p', { class: 'pur-note', text: 'Сметката е по записаните редове на доставката. Всеки разход се разделя по дела на реда в стойността; остатъкът от закръглянето отива на най-големия ред.' }));
  };

  const save = async (btn) => {
    btn.disabled = true;
    try {
      await api('/purchases/' + d.id + '/costs', { method: 'PUT', body: JSON.stringify({ costs: costs.map((c) => ({ kind: c.kind || 'transport', amount: c.amount, file_id: c.file_id })) }) });
      toast(d.status === 'confirmed' ? 'Разходите са разпределени — цените в склада са обновени.' : 'Разходите са записани — влизат в цената при потвърждаване.');
      closeModal();
      openDoc(d.id);
      refreshList();
    } catch (e) { toast(e.message, true); btn.disabled = false; }
  };

  const download = () => {
    const r = calc();
    const rows = r.rows.map((x) => ({ 'Стока': x.description, 'Стойност €': x.value, 'Дял %': x.share, 'Транспорт': x.transport, 'Мито': x.customs, 'Други': x.other, 'Нова стойност €': x.newValue, 'Кол.': x.qty, 'Мярка': x.unit, 'Нова ед. цена €': x.unitPrice }));
    rows.push({ 'Стока': 'Общо', 'Стойност €': r.totals.value, 'Транспорт': r.totals.transport, 'Мито': r.totals.customs, 'Други': r.totals.other, 'Нова стойност €': r.totals.newValue });
    xlsxDownload(rows, 'Разпределение', 'Разпределение_' + String(d.number || d.id).replace(/[\\/:*?"<>|]+/g, '_') + '.xlsx');
  };

  box.appendChild(rowsBox);
  box.appendChild(el('div', { class: 'pur-line-btns' },
    el('button', { class: 'btn sm ghost', type: 'button', text: '+ Разход', onClick: () => { costs.push({ kind: 'transport', amount: '', file_id: atts[0].file_id }); draw(); } }),
    el('button', { class: 'btn sm', type: 'button', text: 'Запази разходите', onClick: (e) => save(e.currentTarget) }),
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Свали разпределението (Excel)', onClick: download })));
  box.appendChild(preview);
  draw();
  return box;
}

async function docDialog(doc) {
  const d = doc || {};
  const isNew = !d.id;
  const locked = !!d.locked;
  const companyId = state().activeCompany;
  const [catalog, contacts, ocrSt, catList] = await Promise.all([
    api('/articles' + cq()).catch(() => []),
    api('/contacts' + cq()).catch(() => null),
    isNew ? null : ocrStatus(companyId),
    // Категориите на разходите (ПРОМТ-31) — само кантората; клиентът не ги вижда.
    // Общите + своите на фирмата (ПРОМТ-34); архивираната своя — само на ред, който вече я има.
    isStaff() ? api('/purchases/categories' + cq()).then((r) => (r && r.categories) || []).catch(() => []) : [],
  ]);
  const categories = Array.isArray(catList) ? catList : [];
  const ocr = (d.ocr && typeof d.ocr === 'object') ? d.ocr : {};
  const editable = !isNew && !locked && d.status !== 'confirmed';
  const ocrOff = !!ocr.disabled || !!(ocrSt && ocrSt.enabled === false);
  const arts = Array.isArray(catalog) ? catalog : [];
  const artLabel = (a) => (a.code ? a.code + ' — ' : '') + (a.name || '');
  const artById = (id) => arts.find((a) => Number(a.id) === Number(id));

  // ---- Лява страна: оригиналът и придружаващите документи ----
  const left = el('div', { class: 'pur-left' });
  const docs = docsPane(d, isNew, locked);
  left.appendChild(docs);

  // ---- Дясна страна: заглавие ----
  // Търсачка, не падащ списък (ПРОМТ-13б): при стотици контакти човек пише
  // част от името или ЕИК и избира от до 20 резултата. Доставчиците са
  // най-отгоре; останалите контакти също се намират.
  let supList = Array.isArray(contacts) ? contacts.slice() : [];
  // Избраният контакт може да го няма в заредения списък (без право
  // „Контакти" или изтрит контакт) — показваме го по името от документа.
  const ensureListed = (id) => {
    if (id && !supList.some((c) => Number(c.id) === Number(id))) supList.push({ id: Number(id), name: d.contact_name || ('#' + id), type: 'supplier' });
  };
  const sortedSuppliers = () => supList.slice().sort((a, b) => (a.type === 'supplier' ? 0 : 1) - (b.type === 'supplier' ? 0 : 1) || String(a.name || '').localeCompare(String(b.name || ''), 'bg'));
  ensureListed(d.contact_id);
  const supplier = combobox({
    items: sortedSuppliers,
    label: (c) => c.name || '',
    sub: (c) => (c.eik ? 'ЕИК: ' + c.eik : ''),
    search: (c) => [c.name, c.eik, c.vat_number].filter(Boolean).join(' '),
    value: d.contact_id,
    placeholder: 'Име или ЕИК на доставчика…',
    emptyText: 'Няма такъв доставчик — създайте го с „Нов доставчик".',
    // ЕИК-ът на избрания доставчик се вижда до него (Макс, 27.10.2026:
    // „ЕИК полето не се попълва") — полето служеше само за търсене и стоеше
    // празно и при избран доставчик.
    onPick: (c) => { if (c) eikInp.value = c.eik || c.vat_number || ''; loadHints(c ? c.id : null); },
  });
  // Най-честите категории за доставчика (ПРОМТ-45): паметта по ЕИК от всички
  // фирми + изключението на фирмата — до 5, най-горе в менюто „Категория".
  let catHints = [];
  async function loadHints(contactId) {
    if (!isStaff() || !categories.length) return;
    let list = [];
    if (contactId != null) {
      try { list = (await api('/purchases/category-hints?company_id=' + companyId + '&contact_id=' + contactId)).hints || []; } catch (e) { list = []; }
    }
    const next = list.map((h) => h.category).filter((id) => categories.some((c) => c.id === id && !c.archived_at));
    // ПРОМТ-46: най-честата категория при доставчика се попълва сама на
    // празните редове без артикул — жълта („предложено"), докато човек не я
    // потвърди или смени. Само в непотвърдена доставка: потвърденото не се
    // пипа само заради отварянето.
    let filled = false;
    if (next.length && !locked && d.status !== 'confirmed') {
      for (const l of lines) {
        if (l.article_id != null || l.advance_of != null || l.category) continue;
        l.category = next[0];
        l.review_fields = (Array.isArray(l.review_fields) ? l.review_fields : []).concat(['category']);
        l.review = true;
        filled = true;
      }
    }
    if (!filled && next.join() === catHints.join()) return;
    catHints = next;
    drawLines();
  }
  const fillSuppliers = (list, selectedId) => {
    supList = list;
    ensureListed(selectedId);
    supplier.setValue(selectedId || null);
    const c = supList.find((x) => Number(x.id) === Number(selectedId));
    if (c && (c.eik || c.vat_number)) eikInp.value = c.eik || c.vat_number;
  };
  const eikInp = input({ type: 'text', inputmode: 'numeric', maxlength: '15', placeholder: 'ЕИК — напишете и „Търси по ЕИК"' });
  {
    const cur = d.contact_id ? supList.find((c) => Number(c.id) === Number(d.contact_id)) : null;
    eikInp.value = (cur && (cur.eik || cur.vat_number)) || d.contact_eik || '';
  }
  const findBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Търси по ЕИК', onClick: async () => {
    const eik = eikInp.value.trim();
    if (!eik) { toast('Въведете ЕИК.', true); eikInp.focus(); return; }
    try {
      const hits = await api('/contacts/find' + cq() + '&eik=' + encodeURIComponent(eik));
      if (hits.length) {
        hits.forEach((h) => { if (!supList.some((c) => c.id === h.id)) supList.push(h); });
        fillSuppliers(supList, hits[0].id);
        toast('Намерен: ' + hits[0].name + '.');
      } else {
        toast('Няма доставчик с ЕИК ' + eik + ' — създайте го с „Нов доставчик".');
      }
    } catch (e) { toast(e.message, true); }
  } });
  // Разчетеният доставчик го няма в контактите: полето за ЕИК и „Нов
  // доставчик" идват попълнени с ЕИК/ДДС/име, за да не се преписва от
  // сканираното на ръка (тест 28.09 — SCAN0023: ЕИК-ът стоеше празен).
  const sug = ocr.suggested_supplier;
  const sugOk = !d.contact_id && sug && typeof sug === 'object' && !locked;
  if (sugOk && sug.eik) eikInp.value = String(sug.eik);
  const supPrefill = () => {
    const eik = eikInp.value.trim();
    // Сменен ЕИК = друг доставчик: името и ДДС № от разчетеното не важат.
    return sugOk && (!eik || eik === String(sug.eik || '')) ? { eik: eik || sug.eik || '', vat: sug.vat || '', name: sug.name || '' } : { eik };
  };
  const newSupBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Нов доставчик', onClick: () => newSupplier(supPrefill(), (c) => { supList.push(c); fillSuppliers(supList, c.id); }) });
  let suggestLine = null;
  if (sugOk) {
    const who = sug.name ? ' („' + sug.name + '“' + (sug.eik ? ', ЕИК ' + sug.eik : '') + ')' : '';
    suggestLine = el('div', { class: 'pur-suggest' },
      el('span', { text: 'Доставчик: не е намерен — създай' + who }),
      el('button', { class: 'btn sm', type: 'button', text: 'Създай доставчик', onClick: () => newSupplier(supPrefill(), (c) => {
        supList.push(c); fillSuppliers(supList, c.id);
        if (suggestLine) suggestLine.remove();
      }) }));
  }
  const supNote = contacts === null ? el('p', { class: 'pur-note', text: 'Нямате право „Контакти" — списъкът с доставчици не се зарежда.' }) : null;

  const docType = el('select', {}, ...DOC_TYPES.map(([v, t]) => el('option', { value: v, text: t })));
  docType.value = d.doc_type || 'invoice';
  const number = input({ type: 'text', value: d.number || '', maxlength: '40' });
  const date = dateInput(d.date || '');
  const taxDate = dateInput(d.tax_event_date || '');
  const currency = input({ type: 'text', value: d.currency || 'EUR', maxlength: '3' });
  const rate = input({ type: 'text', inputmode: 'decimal', value: commaIn(d.exchange_rate != null ? d.exchange_rate : 1) });
  const syncRate = () => { const eur = currency.value.trim().toUpperCase() === 'EUR' || !currency.value.trim(); rate.disabled = eur || locked; if (eur) rate.value = '1'; };
  currency.addEventListener('input', syncRate);
  const vatRate = el('select', {}, ...VAT_RATES.map((v) => el('option', { value: String(v), text: v + ' %' })));
  vatRate.value = String(d.vat_rate != null ? d.vat_rate : 20);
  const payment = el('select', {}, el('option', { value: 'bank', text: 'По банка' }), el('option', { value: 'cash', text: 'В брой' }));
  payment.value = d.payment_method || 'bank';
  const treatment = el('select', {}, ...TREATMENTS.map(([v, t]) => el('option', { value: v, text: t })));
  treatment.value = d.tax_treatment || 'domestic';
  // Правото на данъчен кредит (ПРОМТ-29): решава статията за Конто — ДДС-ът
  // без право отива в разхода. При частичния (ОТГОВОР-ПРОМТ-32) целият ДДС
  // е по 4531 с вид сделка 06, както в Конто; процентът остава за справка —
  // делението по коефициента е в декларацията.
  const vatCredit = el('select', {}, ...VAT_CREDITS.map(([v, t]) => el('option', { value: v, text: t })));
  vatCredit.value = d.vat_credit || 'full';
  const vatPct = input({ type: 'text', inputmode: 'decimal', value: d.vat_credit_pct != null ? commaIn(d.vat_credit_pct) : '', maxlength: '6', placeholder: 'напр. 85' });
  vatPct.title = 'За справка: в статията за Конто целият ДДС е по 4531 (вид сделка 06); делението по коефициента е в декларацията.';
  const pctField = field('Кредит %', vatPct, { size: 's' });
  const syncCredit = () => { pctField.style.display = vatCredit.value === 'partial' ? '' : 'none'; };
  vatCredit.addEventListener('change', syncCredit);
  const refDoc = el('select', {}, el('option', { value: '', text: '— изберете документ —' }),
    ..._rows.filter((x) => x.status === 'confirmed' && x.id !== d.id && x.doc_type === 'invoice').map((x) => el('option', { value: String(x.id), text: '№ ' + (x.number || x.id) + ' — ' + (x.contact_name || '') + ' — ' + isoToBg(x.date) })));
  if (d.ref_doc_id && !refDoc.querySelector('option[value="' + d.ref_doc_id + '"]')) refDoc.appendChild(el('option', { value: String(d.ref_doc_id), text: 'документ #' + d.ref_doc_id }));
  refDoc.value = d.ref_doc_id ? String(d.ref_doc_id) : '';
  const refField = field('Към документ', refDoc);
  const syncRef = () => { refField.style.display = (docType.value === 'credit' || docType.value === 'debit') ? '' : 'none'; };
  docType.addEventListener('change', () => { syncRef(); syncAdvance(); recheck(); });
  // Авансова фактура от доставчика (ПРОМТ-44): платен аванс — без разход и без
  // склад, в Конто по 402 (по избора на фирмата). Отметката я слага само
  // кантората — клиентът не я вижда (сървърът и без това не я приема от него).
  const office = !isClient();
  const advanceCb = el('input', { type: 'checkbox', checked: !!d.advance, 'aria-label': 'Авансова фактура (платен аванс, без разход и без склад)' });
  const advField = office ? el('div', { class: 'field pur-advance' },
    el('label', { class: 'choice' }, advanceCb, el('span', { text: 'Авансова фактура (платен аванс, без разход и без склад)' }))) : null;
  // „Приспадни аванс": ред с минус, който сочи авансовата (advance_of).
  const deductBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Приспадни аванс', onClick: () => pickAdvance() });
  function syncAdvance() {
    const inv = docType.value === 'invoice';
    if (advField) advField.style.display = inv ? '' : 'none';
    if (!inv) advanceCb.checked = false;
    deductBtn.style.display = office && inv && !advanceCb.checked && !locked ? '' : 'none';
  }
  advanceCb.addEventListener('change', () => {
    // Празният първи ред подсказва какво се пише в авансовата.
    if (advanceCb.checked && lines.length === 1 && !String(lines[0].description || '').trim()) { lines[0].description = 'Аванс по договор/оферта № '; drawLines(); }
    if (advHint) advHint.remove();
    syncAdvance();
  });
  async function pickAdvance() {
    const sid = supplier.getValue();
    if (sid == null) { toast('Първо изберете доставчика.', true); return; }
    let list;
    try {
      list = (await api('/purchases/open-advances?company_id=' + companyId + '&contact_id=' + sid + (d.id ? '&except=' + d.id : ''))).advances || [];
    } catch (e) { toast(e.message, true); return; }
    if (!list.length) { toast('Този доставчик няма потвърдени авансови фактури с остатък.', true); return; }
    const box = el('div', { class: 'pick-list' }, ...list.map((x) => el('button', { class: 'pick-row', type: 'button', onClick: () => {
      // Ред с минус: основата и ДДС-ът на окончателната са след
      // приспадането; сумата може да се намали за частично приспадане.
      lines = lines.filter((l) => String(l.description || '').trim() || num(l.unit_price) || l.article_id != null);
      lines.push({ description: 'Приспадане на аванс по ф-ра № ' + (x.number || x.id) + ' от ' + isoToBg(x.date), quantity: 1, unit_price: -x.available, discount: 0, advance_of: x.id });
      engaged = true;
      closeModal();
      drawLines({ i: lines.length - 1, key: 'desc' }); recheck();
      linesBody.dispatchEvent(new Event('input', { bubbles: true }));
      toast('Приспаднат аванс ' + amount(x.available) + ' по ф-ра № ' + (x.number || x.id) + ' — сметнете отново основата, ДДС-а и общото.');
    } },
      el('span', { style: 'font-weight:600', text: '№ ' + (x.number || x.id) }),
      el('span', { text: isoToBg(x.date) }),
      el('span', { style: 'color:var(--muted);margin-left:auto', text: '(остават ' + amount(x.available) + ' ' + (x.currency || 'EUR') + ')' }))));
    openModal({ title: 'Приспадни аванс', body: box, foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal })) });
  }
  const notes = el('textarea', { rows: '2', maxlength: '2000' });
  notes.value = d.notes || '';

  // Работният месец (ДДС периодът). Сам се определя от датата; човекът го
  // сменя само когато фактурата трябва да влезе в по-късен месец. Пращаме
  // го само ако е пипнат — иначе черновата следва датата.
  const perCo = { purchases_closed_through: (_periods && _periods.closed_through) || '' };
  const perToday = (_periods && _periods.today) || new Date().toISOString().slice(0, 10);
  const periodSel = el('select', {});
  let periodTouched = false;
  const fillPeriods = () => {
    const iso = date.dataset.iso || d.date || '';
    const opts = pp.choices(perCo, iso, perToday);
    if (d.period && !opts.includes(d.period)) opts.unshift(d.period);
    const keep = periodTouched ? periodSel.value : (d.period_manual ? d.period : pp.openPeriodFor(perCo, iso, perToday));
    clear(periodSel);
    opts.forEach((m) => periodSel.appendChild(el('option', { value: m, text: pp.bg(m) })));
    periodSel.value = opts.includes(keep) ? keep : opts[0] || '';
  };
  fillPeriods();
  periodSel.addEventListener('change', () => { periodTouched = true; });
  date.addEventListener('change', () => { if (!periodTouched) fillPeriods(); });
  const numbersLine = (d.seq_no || d.cc_no) ? el('p', { class: 'pur-numbers' },
    d.seq_no ? el('span', { text: 'Пор. № ' + d.seq_no }) : null,
    d.cc_no ? el('span', { text: 'СС ' + d.cc_no }) : null) : null;

  // ---- Редове ----
  let lines = (Array.isArray(d.lines) ? d.lines : []).map((l) => Object.assign({}, l));
  // ПРОМТ-19, т. 4: на празна форма проверката на сумите „спи" — иначе
  // човек вижда „✗ Общата сума трябва да е положителна", преди да е въвел
  // каквото и да е. Събужда се при число в сумите или редовете и при
  // „Запази". Документ със суми (OCR, записан) е буден от самото начало.
  let engaged = [d.subtotal, d.vat_amount, d.total].some((v) => num(v) !== 0);
  if (!lines.length) lines.push({ description: '', quantity: 1, unit_price: 0, discount: 0 });
  // Нов празен ред носи най-честата категория при доставчика (ПРОМТ-46) —
  // жълта, като предложение; без памет — без категория (не „Други").
  const blankLine = () => {
    const nl = { description: '', quantity: 1, unit_price: 0, discount: 0 };
    if (catHints.length && !locked && d.status !== 'confirmed') Object.assign(nl, { category: catHints[0], review_fields: ['category'], review: true });
    return nl;
  };
  // Разчетеният текст говори за аванс (ПРОМТ-44): отметката се ПРЕДЛАГА, не
  // се слага сама — „аванс" стои и в окончателни фактури („приспаднат аванс").
  const saysAdvance = office && !d.advance && d.status !== 'confirmed' && !locked && (d.doc_type || 'invoice') === 'invoice'
    && lines.some((l) => /аванс/i.test(String(l.description || '') + ' ' + String(l.ocr_text || '')) && l.advance_of == null);
  const advHint = saysAdvance ? el('div', { class: 'sev-warn pur-adv-hint', role: 'status' },
    el('span', { text: 'В текста пише „аванс" — ако това е авансова фактура (платен аванс), сложете отметката „Авансова фактура".' }), ' ',
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Да, авансова е', onClick: () => { advanceCb.checked = true; advanceCb.dispatchEvent(new Event('change')); } })) : null;
  const dl = el('datalist', { id: 'pur-arts-' + Date.now() }, ...arts.map((a) => el('option', { value: artLabel(a) })));
  const reviewSeq = ++_reviewSeq;
  // Жълтата клетка: клас + скрит текст „защо“, вързан с aria-describedby —
  // цветът сам не стига за четеца (и за който не различава жълто).
  const markReview = (l, n, fieldKey, node) => {
    if (!Array.isArray(l.review_fields) || !l.review_fields.includes(fieldKey)) return null;
    const hint = el('span', { class: 'sr-only', id: 'pur-rv-' + reviewSeq + '-' + n + '-' + fieldKey, text: 'Разчитането не е сигурно за ' + REVIEW_NAMES[fieldKey] + ' на ред ' + n + ' — провери.' });
    node.classList.add('cell-review');
    node.setAttribute('aria-describedby', hint.id);
    node._reviewHint = hint;
    return hint;
  };
  // Ръчната редакция = човекът е видял полето: махаме жълтото и полето от
  // review_fields, за да не се върне след запис (сървърът пази списъка).
  const clearReview = (l, fieldKey, node) => {
    if (Array.isArray(l.review_fields) && l.review_fields.includes(fieldKey)) {
      l.review_fields = l.review_fields.filter((f) => f !== fieldKey);
      if (!l.review_fields.length) l.review = false;
    }
    if (node && node.classList.contains('cell-review')) {
      node.classList.remove('cell-review');
      node.removeAttribute('aria-describedby');
      if (node._reviewHint) { node._reviewHint.remove(); node._reviewHint = null; }
    }
  };
  const linesBody = el('tbody');
  // Esc в ред: излиза от реда — без ограждане и без фокус, диалогът остава
  // отворен; така текстът във фактурата се маркира и копира свободно
  // (Макс, 29.10.2026). Второ Esc (вече извън реда) затваря диалога както досега.
  linesBody.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const t = e.target;
    // отворен списък/падащо меню затваря първо себе си
    if (t && t.getAttribute && t.getAttribute('aria-expanded') === 'true') return;
    e.preventDefault();
    e.stopPropagation();
    linesBody.querySelectorAll('tr.pur-row-on').forEach((x) => x.classList.remove('pur-row-on'));
    if (docs && docs.highlight) docs.highlight(null);
    if (t && t.blur) t.blur();
  });
  // Редът, в който е човекът, се огражда във фактурата отляво (29.10.2026).
  linesBody.addEventListener('focusin', (e) => {
    const tr = e.target && e.target.closest && e.target.closest('tr[data-i]');
    if (!tr) return;
    linesBody.querySelectorAll('tr.pur-row-on').forEach((x) => x.classList.remove('pur-row-on'));
    tr.classList.add('pur-row-on');
    const l = lines[Number(tr.dataset.i)];
    if (typeof docs !== 'undefined' && docs.highlight) docs.highlight(l && Array.isArray(l.ocr_box) ? l.ocr_box : null);
  });
  const unitSelect = (l, i) => {
    const a = l.article_id != null ? artById(l.article_id) : null;
    if (!a) {
      const u = input({ type: 'text', value: l.unit || '', maxlength: '30', 'aria-label': 'Мярка, ред ' + (i + 1) });
      u.addEventListener('input', () => { l.unit = u.value; clearReview(l, 'unit', u); });
      return u;
    }
    const units = [a.measure || ''].concat((a.conversions || []).map((c) => c.unit)).filter(Boolean);
    // Материал в кг/л се купува и „на бройка" (разфасовка на реда): мярката
    // „бр." и тази от фактурата остават за избор — иначе 10 бр. от фактурата
    // тихо ставаха 10 кг.
    if (isMetric(a.measure)) {
      if (!units.some((u) => u.replace(/\.$/, '') === 'бр')) units.push('бр.');
      if (l.unit && !units.includes(l.unit)) units.push(l.unit);
    }
    const s = el('select', { 'aria-label': 'Мярка, ред ' + (i + 1) }, ...units.map((u) => el('option', { value: u, text: u })));
    s.value = units.includes(l.unit) ? l.unit : units[0] || '';
    l.unit = s.value;
    s.addEventListener('change', () => { l.unit = s.value; clearReview(l, 'unit', s); drawLines({ i, key: 'unit' }); recheck(); });
    return s;
  };
  // „1 бр. = … кг" — колко е разфасовката (services/packs.js). Попълва се от
  // описанието („400г") или от предишна доставка; човекът я поправя.
  const packNode = (l, n) => {
    const a = l.article_id != null ? artById(l.article_id) : null;
    if (!needsPack(a, l.unit)) { delete l.pack; return null; }
    if (!(Number(l.pack) > 0)) {
      const guess = parsePack(l.description, a.measure);
      if (guess) l.pack = guess;
    }
    const total = el('span', { class: 'pur-pack-total' });
    const show = () => {
      const p = Number(l.pack) > 0 ? Number(l.pack) : 0;
      total.textContent = p ? '= ' + String(Math.round(num(l.quantity) * p * 1000) / 1000).replace('.', ',') + ' ' + a.measure : 'колко ' + a.measure + ' е 1 ' + (l.unit || 'бр.') + '?';
      total.classList.toggle('bad', !p);
    };
    const inp = input({ type: 'text', inputmode: 'decimal', class: 'pur-pack', value: Number(l.pack) > 0 ? commaIn(l.pack) : '', placeholder: '0,5',
      'aria-label': 'Разфасовка — колко ' + a.measure + ' е 1 ' + (l.unit || 'бр.') + ', ред ' + n });
    inp.addEventListener('input', () => { const v = num(inp.value); if (v > 0) l.pack = v; else delete l.pack; show(); });
    show();
    const wrap = el('div', { class: 'pur-pack-wrap' }, el('span', { text: '1 ' + (l.unit || 'бр.') + ' =' }), inp, el('span', { text: a.measure }), total);
    wrap._input = inp;
    wrap._show = show;
    return wrap;
  };
  // Прерисуването не бива да мести екрана: след „✓ Проверен“ или нов ред
  // човекът е на същия ред, не най-горе (Макс, 29.10.2026). focusAt — кой
  // ред и кое поле да получи фокуса след прерисуването.
  // Всички превъртащи се рамки над редовете (диалогът, страницата, лентата на
  // таблицата) — мястото се пази на всяка; иначе Enter в „Артикул“ връщаше
  // най-горе (Макс, 29.10.2026: пазеше се само лентата на таблицата).
  function scrollers(node) {
    const out = [];
    for (let p = node && node.parentElement; p; p = p.parentElement) {
      if (p.scrollTop || p.scrollHeight > p.clientHeight) out.push([p, p.scrollTop, p.scrollLeft]);
    }
    const se = node && node.ownerDocument && node.ownerDocument.scrollingElement;
    if (se && !out.some(([p]) => p === se)) out.push([se, se.scrollTop, se.scrollLeft]);
    return out;
  }
  function drawLines(focusAt) {
    const kept = scrollers(linesBody);
    // докато редовете ги няма, височината остава (на рамката — tbody не държи min-height),
    // иначе диалогът се свива и браузърът го връща нагоре
    const wrap = linesBody.closest ? linesBody.closest('.pur-lines-wrap') : null;
    if (wrap && wrap.offsetHeight) wrap.style.minHeight = wrap.offsetHeight + 'px';
    clear(linesBody);
    const focusables = [];
    lines.forEach((l, i) => {
      const n = i + 1;
      const desc = input({ type: 'text', value: l.description || '', maxlength: '500', 'aria-label': 'Описание, ред ' + n });
      desc.addEventListener('input', () => { l.description = desc.value; clearReview(l, 'description', desc); });
      const a = l.article_id != null ? artById(l.article_id) : null;
      const art = input({ type: 'text', value: a ? artLabel(a) : '', list: dl.id, placeholder: 'код или име', 'aria-label': 'Артикул, ред ' + n });
      art.addEventListener('change', () => {
        const v = art.value.trim().toLowerCase();
        if (!v) { delete l.article_id; drawLines({ i, key: 'art' }); recheck(); return; }
        const hit = arts.find((x) => artLabel(x).toLowerCase() === v || String(x.code || '').toLowerCase() === v || String(x.name || '').toLowerCase() === v);
        if (!hit) { art.classList.add('bad'); art.title = 'Изберете артикул от каталога'; toast('„' + art.value + '" не е в каталога — изберете от списъка или оставете празно за разход.', true); return; }
        l.article_id = hit.id;
        // Изборът от човек снема жълтото от „Артикул" — само той: попълненият
        // от OCR-а артикул (предложен от модела, по името) остава жълт, докато
        // човек не го избере сам (рецензия 27.10.2026).
        clearReview(l, 'article', null);
        if (!String(l.description || '').trim()) l.description = hit.name || '';
        // „бр." от фактурата при материал в кг остава — става разфасовка.
        if (!needsPack(hit, l.unit)) l.unit = hit.measure || '';
        drawLines({ i, key: 'art' }); recheck();
      });
      const qty = input({ type: 'text', inputmode: 'decimal', value: commaIn(l.quantity != null ? l.quantity : 1), 'aria-label': 'Количество, ред ' + n });
      const price = input({ type: 'text', inputmode: 'decimal', value: fPrice(l.unit_price != null ? l.unit_price : 0), 'aria-label': 'Единична цена, ред ' + n });
      // Точната цена се сменя само когато човек пипне самата цена — иначе
      // промяна на количеството би я заменила с показаната (закръглена).
      let priceTouched = false;
      const disc = input({ type: 'text', inputmode: 'decimal', value: commaIn(l.discount || 0), 'aria-label': 'Отстъпка %, ред ' + n });
      const value = el('output', { class: 'num', 'aria-label': 'Стойност, ред ' + n, text: amount(lineValue(l)) });
      const onNum = (e) => { engaged = true; if (e && e.target === price) priceTouched = true; l.quantity = num(qty.value); if (priceTouched) l.unit_price = num(price.value); l.discount = num(disc.value); value.textContent = amount(lineValue(l)); recheck(); };
      [qty, price, disc].forEach((x) => x.addEventListener('input', onNum));
      // Стойността на реда не се въвежда — тя е кол. × цена. Затова
      // съмнението в нея се снема, когато човек пипне някое от тях.
      qty.addEventListener('input', () => { clearReview(l, 'quantity', qty); clearReview(l, 'subtotal', value); });
      price.addEventListener('input', () => { clearReview(l, 'unit_price', price); clearReview(l, 'subtotal', value); });
      // Гориво (Макс, 30.10.2026): на бона има само цена с ДДС и акциз. Пише се тя —
      // програмата маха ДДС-а; акцизът остава в цената без ДДС (част е от разхода).
      const fuelCat = !l.article_id && categories.find((c) => c.id === l.category && c.qty);
      let fuelGross = null;
      if (fuelCat && !locked) {
        // ред без ДДС: цената от бона е и цената без ДДС
        const vr = () => { if (Number(l.vat_rate) === 0 && l.vat_rate != null && l.vat_rate !== '') return 0; const r = num(vatRate.value); return Number.isFinite(r) ? r : 20; };
        const g = input({ type: 'text', inputmode: 'decimal', class: 'pur-gross',
          value: l.unit_price ? commaIn(Math.round(num(l.unit_price) * (1 + vr() / 100) * 10000) / 10000) : '',
          placeholder: 'с ДДС', title: 'Цена на литър с ДДС и акциз, както е на бона — цената без ДДС се смята сама',
          'aria-label': 'Цена с ДДС (от бона), ред ' + n });
        g.addEventListener('input', () => {
          const gv = num(g.value);
          if (!(gv > 0)) return;
          l.unit_price = Math.round((gv / (1 + vr() / 100)) * 1e6) / 1e6;
          price.value = fPrice(l.unit_price);
          priceTouched = false;
          engaged = true;
          clearReview(l, 'unit_price', price); clearReview(l, 'subtotal', value);
          value.textContent = amount(lineValue(l)); recheck();
        });
        fuelGross = el('label', { class: 'pur-gross-wrap' }, el('span', { class: 'muted', text: 'с ДДС: ' }), g);
      }
      disc.addEventListener('input', () => clearReview(l, 'subtotal', value));
      // Сметката е само за ред БЕЗ артикул (услуга, транспорт, наем): в коя
      // разходна сметка да го осчетоводи Конто. Ред с артикул взема сметките
      // от артикула (групата му) — полето е заключено и го казва.
      const acct = input({ type: 'text', value: l.expense_account || '', maxlength: '30', list: ACCOUNTS_ID, placeholder: a ? 'от артикула' : '602', title: a ? 'Сметките идват от артикула' : 'Разходна сметка в Конто — напр. 602 външни услуги', 'aria-label': 'Разходна сметка, ред ' + n });
      acct.addEventListener('input', () => { l.expense_account = acct.value; });
      acct.disabled = !!a || locked;
      // Категорията на разхода (ПРОМТ-31): по нея CRM-ът намира статията на
      // фирмата в Конто (165/166/167 са различни във всяка фирма). Само за
      // ред без артикул; сметката без „Сметка за разход" идва от категорията.
      // ПРОМТ-46: търсачка с групи (Материали 601 / Услуги 602 / Други 609)
      // вместо падащо меню. При празно поле най-отгоре са „Предложени" —
      // паметта по ЕИК на доставчика, после разчетената от OCR категория.
      const ocrCat = Array.isArray(l.review_fields) && l.review_fields.includes('category') ? l.category : null;
      const catBox = categoryCombo({
        categories,
        value: (!a && l.category && categories.some((c) => c.id === l.category)) ? l.category : '',
        suggested: () => catHints.concat(ocrCat && !catHints.includes(ocrCat) ? [ocrCat] : []),
        ariaLabel: 'Категория на разхода, ред ' + n,
        placeholder: a ? 'от артикула' : 'Пишете — напр. „наем"',
        onPick: (c) => onCat(c),
      });
      const catSel = catBox.input;
      catSel.title = 'Категория на разхода — по нея се намира статията в Конто на фирмата';
      const onCat = (picked) => {
        const prev = categories.find((x) => x.id === l.category);
        l.category = picked ? picked.id : '';
        const nowFuel = !!categories.find((x) => x.id === l.category && x.qty);
        clearReview(l, 'category', catSel);
        const c = picked;
        // Сметката следва категорията (Макс, 30.10.2026), освен ако човекът е
        // написал своя: празна или тази на старата категория се сменя.
        if (!acct.value || (prev && acct.value === String(prev.account))) {
          acct.value = c ? String(c.account || '') : '';
          l.expense_account = acct.value;
        }
        if (c && !acct.value) acct.placeholder = c.account;
        // застраховката е освободена от ДДС (чл. 47) — на фактура с ДДС
        const toExempt = l.category === 'zastr' && num(vatRate.value) > 0 && Number(l.vat_rate) !== 0;
        if (toExempt) { l.vat_rate = 0; recheck(); }
        // полето „с ДДС“ се появява/изчезва с горивото
        if (toExempt || nowFuel !== !!(prev && prev.qty)) drawLines({ i, key: 'desc' });
      };
      catBox.setDisabled(!!a || locked || !categories.length);
      if (!a && l.category) { const c = categories.find((x) => x.id === l.category); if (c && !acct.value) acct.placeholder = c.account; }
      const del = el('button', { class: 'btn sm ghost', type: 'button', 'aria-label': 'Премахни ред ' + n, text: '×', onClick: () => { lines.splice(i, 1); drawLines({ i: Math.max(0, i - 1), key: 'desc' }); recheck(); } });
      // Нов ред точно под този — пропуснат от OCR-а ред се вмъква на мястото си,
      // не най-долу (Макс, 29.10.2026).
      const addBelow = locked ? null : el('button', { class: 'btn sm ghost pur-add-below', type: 'button', 'aria-label': 'Нов ред под ред ' + n, title: 'Нов ред под този', text: '+',
        onClick: () => {
          lines.splice(i + 1, 0, blankLine());
          engaged = true;
          drawLines({ i: i + 1, key: 'desc' }); recheck();
        } });
      const unitNode = unitSelect(l, i);
      const pack = packNode(l, n);
      if (pack) qty.addEventListener('input', () => pack._show());
      if (pack) desc.addEventListener('change', () => { if (!(Number(l.pack) > 0)) drawLines({ i, key: 'desc' }); });
      const cells = [desc, art, qty, unitNode, price, disc, value, catBox, acct, del];
      if (locked) cells.forEach((c) => { if (c === catBox) catBox.setDisabled(true); else if (c.tagName !== 'OUTPUT') c.disabled = true; });
      // Приспаднатият аванс: само сумата се пипа (частично приспадане) —
      // без артикул, категория и сметка: в Конто той е по 402 (401).
      if (l.advance_of != null) { [art, qty, unitNode, disc, acct].forEach((c) => { c.disabled = true; }); catBox.setDisabled(true); }
      if (locked && pack) pack._input.disabled = true;
      const hints = new Map([[desc, 'description'], [art, 'article'], [qty, 'quantity'], [unitNode, 'unit'], [price, 'unit_price'], [value, 'subtotal']]);
      if (!a) hints.set(catBox, 'category');
      // Отгатната категория (наем, гориво…), с която човекът е съгласен —
      // същото „✓“ като при артикула: изборът на същата стойност не е промяна.
      // „без ДДС“: ред, освободен от ДДС (застраховка, чл. 47), на фактура с ДДС
      const exemptCb = el('input', { type: 'checkbox', 'aria-label': 'Без ДДС (освободен), ред ' + n });
      exemptCb.checked = Number(l.vat_rate) === 0 && l.vat_rate !== '' && l.vat_rate != null;
      exemptCb.disabled = locked;
      exemptCb.addEventListener('change', () => { if (exemptCb.checked) l.vat_rate = 0; else delete l.vat_rate; engaged = true; recheck(); if (fuelGross) drawLines({ i, key: 'desc' }); });
      // на всеки ред — и с артикул, и гориво (Макс, 30.10.2026: „да избирам дали е с ДДС или без“)
      const exemptBox = el('label', { class: 'pur-exempt', title: 'Сумата е освободена от ДДС (напр. застраховка по чл. 47) — не влиза в ДДС основата' }, exemptCb, el('span', { text: ' без ДДС' }));
      const catExtra = (!a && !locked && Array.isArray(l.review_fields) && l.review_fields.includes('category'))
        ? el('button', { class: 'btn sm ghost pur-cat-ok', type: 'button', text: '✓', title: 'Категорията е вярна', 'aria-label': 'Категорията на ред ' + n + ' е проверена',
          onClick: () => { clearReview(l, 'category', catSel); drawLines({ i, key: 'desc' }); recheck(); } })
        : null;
      // Кодът на съпоставения артикул се вижда отделно: в полето той може да
      // се отреже, а по него счетоводителят сверява с Конто.
      const artExtra = [];
      if (a && a.code) artExtra.push(el('span', { class: 'pur-art-code', text: 'код ' + a.code }));
      // Предложен (жълт) артикул, с който човекът е съгласен: без този бутон
      // няма как да го „одобри" — изборът на същата стойност не е промяна и
      // редът оставаше жълт и не учеше паметта (втора рецензия 28.10.2026).
      if (a && !locked && Array.isArray(l.review_fields) && l.review_fields.includes('article')) {
        artExtra.push(el('button', { class: 'btn sm ghost pur-art-ok', type: 'button', text: '✓ Проверен', 'aria-label': 'Артикулът на ред ' + n + ' е проверен',
          onClick: () => { clearReview(l, 'article', art); drawLines({ i, key: 'art' }); recheck(); } }));
      }
      if (!a && d.status === 'ocr_review' && !locked) {
        artExtra.push(el('button', { class: 'btn sm ghost pur-new-art', type: 'button', text: 'Нов артикул', 'aria-label': 'Нов артикул от ред ' + n,
          onClick: () => newArticle(l, (created) => {
            arts.push(created);
            dl.appendChild(el('option', { value: artLabel(created) }));
            l.article_id = created.id;
            clearReview(l, 'article', null);
            l.unit = created.measure || l.unit || '';
            drawLines({ i, key: 'art' }); recheck();
          }) }));
      }
      focusables[i] = { desc, art, unit: unitNode };
      linesBody.appendChild(el('tr', { 'data-i': String(i), class: Array.isArray(l.ocr_box) ? 'pur-has-box' : null },
        el('td', { class: 'num pur-ln', text: String(n) }),
        ...cells.map((c) => el('td', { class: c === del ? 'pur-row-btns' : null }, c,
          // жълтото на категорията е на самото поле, не на обвивката му
          hints.has(c) ? markReview(l, n, hints.get(c), c === catBox ? catSel : c) : null,
          c === art ? artExtra : null,
          c === catBox ? catExtra : null,
          c === catBox ? exemptBox : null,
          c === price ? fuelGross : null,
          c === unitNode ? pack : null,
          c === del ? addBelow : null))));
    });
    const f = focusAt && focusables[focusAt.i] && focusables[focusAt.i][focusAt.key];
    if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
    if (wrap) wrap.style.minHeight = '';
    kept.forEach(([p, t, l]) => { p.scrollTop = t; p.scrollLeft = l; });
  }

  // ---- Суми ----
  const subtotal = input({ type: 'text', inputmode: 'decimal', value: f2in(d.subtotal || 0) });
  const vatAmount = input({ type: 'text', inputmode: 'decimal', value: f2in(d.vat_amount || 0) });
  const total = input({ type: 'text', inputmode: 'decimal', value: f2in(d.total || 0) });
  const check = el('div', { class: 'pur-check', role: 'status', 'aria-live': 'polite', id: 'pur-check-' + reviewSeq });
  let confirmBtn = null;
  function totalsNow() {
    return { lines, subtotal: num(subtotal.value), vat_amount: num(vatAmount.value), total: num(total.value), vat_rate: num(vatRate.value), doc_type: docType.value };
  }
  function recheck() {
    const errs = checkTotals(totalsNow());
    clear(check);
    if (!engaged) {
      check.classList.remove('ok', 'bad');
      check.appendChild(el('span', { text: 'Попълнете сумите — проверката ще се появи тук.' }));
      if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.setAttribute('aria-disabled', 'true'); }
      return;
    }
    check.classList.toggle('ok', !errs.length);
    check.classList.toggle('bad', !!errs.length);
    if (!errs.length) {
      check.appendChild(el('span', { text: '✓ Сборовете и ДДС-ът съвпадат с редовете.' }));
      const rd = roundingDiff(totalsNow());
      if (rd) check.appendChild(el('div', { class: 'pur-round', text: 'Разлика от закръгляне: ' + amount(r2(Math.abs(rd))) + ' €' }));
    } else errs.forEach((t) => check.appendChild(el('div', { text: '✗ ' + t })));
    // „Потвърди“ само при сверени сборове: сървърът и без това ще откаже,
    // но разчетеният документ не бива да изглежда готов, докато не е.
    if (confirmBtn) {
      confirmBtn.disabled = !!errs.length;
      confirmBtn.setAttribute('aria-disabled', errs.length ? 'true' : 'false');
    }
  }
  [subtotal, vatAmount, total].forEach((x) => x.addEventListener('input', () => { if (String(x.value).trim() !== '') engaged = true; recheck(); }));
  vatRate.addEventListener('change', recheck);
  const fromLines = el('button', { class: 'btn sm ghost', type: 'button', text: 'Сметни от редовете', onClick: () => {
    const s = r2(lines.reduce((acc, l) => acc + lineValue(l), 0));
    const v = r2(s * num(vatRate.value) / 100);
    subtotal.value = f2in(s); vatAmount.value = f2in(v); total.value = f2in(s + v);
    subtotal.dispatchEvent(new Event('input', { bubbles: true }));
  } });

  const addLine = (extra) => el('button', { class: 'btn sm ghost', type: 'button', text: extra ? '+ Ред без артикул (разход)' : '+ Ред', onClick: () => {
    lines.push(extra ? { description: 'Транспорт', quantity: 1, unit_price: 0, discount: 0 } : blankLine());
    drawLines({ i: lines.length - 1, key: 'desc' }); recheck();
    linesBody.dispatchEvent(new Event('input', { bubbles: true }));
  } });

  // Лентите на OCR-а: изключен, неуспешно разчитане, предупреждения от
  // валидацията. Само на непотвърден документ — след потвърждаването те
  // вече не са задача.
  function ocrBanners() {
    if (isNew || d.status === 'confirmed') return null;
    const out = [];
    if (pending(d) && !ocrOff) {
      out.push(el('div', { class: 'sev-warn pur-reading-bar', role: 'status', text: 'Разчита се… обикновено отнема 1–5 минути. Екранът ще се обнови сам.' }));
    }
    // „Въведи документа ръчно" е за кантората — клиентът само качва (Д6).
    if (ocrOff) { if (!isClient()) out.push(ocrOffBanner()); } else if (ocr.error && !locked) {
      out.push(el('div', { class: 'sev-warn pur-ocr-err', role: 'status' },
        el('span', { text: 'Разчитането не успя — опитай пак или въведи ръчно' }), ' ',
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Разчети пак', onClick: reread })));
    }
    // Същият доставчик, номер и година вече има (ПРОМТ-34) — само
    // предупреждение; потвърждаването ще спре.
    const dup = d.probable_duplicate;
    if (dup) {
      out.push(el('div', { class: 'sev-warn pur-dup', role: 'status' },
        el('span', { text: dupText(dup) + ' Проверете дали не е същата — ако е, изтрийте тази чернова.' }), ' ',
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Отвори качената', onClick: () => { closeModal(); openDoc(dup.id); } })));
    }
    const warns = Array.isArray(ocr.warnings) ? ocr.warnings : (Array.isArray(d.warnings) ? d.warnings : []);
    if (warns.length) {
      out.push(el('div', { class: 'sev-warn', role: 'status' },
        el('b', { text: 'Провери разчетеното:' }),
        el('ul', { class: 'pur-warns' }, ...warns.map((w) => el('li', { text: String(w) })))));
    }
    return out.length ? el('div', {}, ...out) : null;
  }
  // Повторно разчитане: сървърът връща обновения документ — показваме него,
  // без второ зареждане.
  async function reread() {
    try {
      const fresh = await api('/purchases/' + d.id + '/ocr', { method: 'POST' });
      closeModal();
      refreshList();
      await docDialog(fresh);
    } catch (e) { toast(e.message, true); }
  }

  const right = el('div', { class: 'pur-right' },
    locked ? el('div', { class: 'sev-warn', role: 'status', text: 'Документът е изнесен към Конто и е заключен — само за четене.' + (isAdmin() ? ' Отключва администратор, с причина.' : '') }) : null,
    isNew ? el('p', { class: 'pur-note', text: 'Ръчно въведената доставка се потвърждава при запис — проверките по-долу трябва да са зелени.' }) : null,
    numbersLine,
    ocrBanners(),
    row(field('Доставчик', supplier)),
    row(field('ЕИК на доставчика', eikInp, { size: 'm' }), el('div', { class: 'pur-eik-btns' }, findBtn, newSupBtn)),
    suggestLine,
    supNote,
    row(field('Вид', docType, { size: 'm' }), field('№', number, { size: 'm' }), field('Дата', date, { size: 's' }), field('Данъчно събитие', taxDate, { size: 's' })),
    advField, advHint,
    row(field('Валута', currency, { size: 's' }), field('Курс към EUR', rate, { size: 's' }), field('ДДС', vatRate, { size: 's' }), field('Плащане', payment, { size: 's' })),
    row(field('Режим', treatment, { size: 'm' }), field('Работен месец (ДДС)', periodSel, { size: 's' }), field('Данъчен кредит', vatCredit, { size: 'm' }), pctField, refField),
    el('div', { class: 'pur-lines-wrap' },
      el('table', { class: 'list pur-lines' },
        el('thead', {}, el('tr', {}, el('th', { class: 'num', text: '№' }), ...['Описание', 'Артикул', 'Кол.', 'Мярка', 'Цена без ДДС', 'Отст. %', 'Стойност', 'Категория', 'Сметка за разход', ''].map((h) => el('th', { text: h })))),
        linesBody),
      dl, accountsList()),
    el('p', { class: 'pur-lines-help' }, 'Артикул — стоката или материалът от каталога: влиза в склада. ',
      el('b', { text: 'Категория' }), ' — само за ред без артикул: видът на разхода (наем, ел. енергия, гориво…). По нея програмата намира статията на фирмата в Конто и сметката (602/601/609). ',
      el('b', { text: 'Сметка за разход' }), ' — само ако трябва друга сметка от тази на категорията, напр. 602 — Разходи за външни услуги. За ред с артикул сметката идва от артикула.'),
    locked ? null : el('div', { class: 'pur-line-btns' }, addLine(false), addLine(true), deductBtn),
    row(field('Основа', subtotal, { size: 's' }), field('ДДС', vatAmount, { size: 's' }), field('Общо', total, { size: 's' }), locked ? null : fromLines),
    check,
    field('Бележка', notes),
    costsSection(d, arts),
    isNew ? null : historyBox(d));

  drawLines();
  if (d.contact_id != null) loadHints(d.contact_id);
  syncRate();
  syncRef();
  syncAdvance();
  syncCredit();
  recheck();
  if (locked) right.querySelectorAll('input,select,textarea').forEach((x) => { x.disabled = true; });

  function payload() {
    return {
      company_id: companyId,
      contact_id: supplier.getValue() != null ? Number(supplier.getValue()) : null,
      doc_type: docType.value,
      number: number.value.trim(),
      date: date.dataset.iso || '',
      tax_event_date: taxDate.dataset.iso || '',
      currency: (currency.value.trim() || 'EUR').toUpperCase(),
      exchange_rate: num(rate.value) || 1,
      vat_rate: num(vatRate.value),
      payment_method: payment.value,
      tax_treatment: treatment.value,
      vat_credit: vatCredit.value,
      vat_credit_pct: vatCredit.value === 'partial' ? num(vatPct.value) : null,
      ref_doc_id: (docType.value === 'credit' || docType.value === 'debit') && refDoc.value ? Number(refDoc.value) : null,
      ...(office ? { advance: docType.value === 'invoice' && advanceCb.checked } : {}),
      notes: notes.value,
      ...(periodTouched ? { period: periodSel.value } : {}),
      lines: lines.map((l) => ({
        description: l.description || '', article_id: l.article_id != null ? l.article_id : null, quantity: num(l.quantity), unit: l.unit || '',
        pack: Number(l.pack) > 0 ? Number(l.pack) : null,
        unit_price: num(l.unit_price), discount: num(l.discount), expense_account: l.article_id != null ? '' : (l.expense_account || ''),
        category: l.article_id != null ? '' : (l.category || ''),
        // ред, освободен от ДДС (застраховка, чл. 47) на фактура с ДДС (30.10.2026)
        ...(Number(l.vat_rate) === 0 && l.vat_rate !== '' && l.vat_rate != null ? { vat_rate: 0 } : {}),
        // Следите от разчитането тръгват обратно: иначе записът би изтрил
        // кои клетки още чакат преглед и какво е чел OCR-ът (supplier_code
        // е ключът за supplier_article_map при потвърждаване).
        review: !!l.review,
        review_fields: Array.isArray(l.review_fields) ? l.review_fields.slice() : [],
        confidence: l.confidence != null && l.confidence !== '' && Number.isFinite(Number(l.confidence)) ? Number(l.confidence) : null,
        supplier_code: l.supplier_code != null ? String(l.supplier_code) : '',
        // какво е прочел OCR-ът — за да се научи поправеното описание
        ...(l.ocr_text ? { ocr_text: String(l.ocr_text) } : {}),
        ...(Array.isArray(l.ocr_box) ? { ocr_box: l.ocr_box.slice() } : {}),
        ...(l.advance_of != null ? { advance_of: l.advance_of } : {}),
      })),
      subtotal: num(subtotal.value), vat_amount: num(vatAmount.value), total: num(total.value),
    };
  }
  function datesOk() {
    // Общата проверка (ПРОМТ-19): хваща и несъществуваща дата (31.02).
    return guardDates(date);
  }
  const done = (saved, msg) => {
    (saved && saved.warnings || []).forEach((w) => toast(w, true));
    toast(msg);
    closeModal();
    refreshList();
  };
  async function save() {
    if (!datesOk()) return null;
    return isNew
      ? api('/purchases', { method: 'POST', body: JSON.stringify(payload()) })
      : api('/purchases/' + d.id, { method: 'PUT', body: JSON.stringify(payload()) });
  }

  const foot = el('div', {});
  foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }));
  // Редовете на цяла ширина: оригиналът се скрива, докато се попълва.
  const wideBtn = el('button', { class: 'btn ghost', type: 'button', 'aria-pressed': 'false', text: '⇔ Редовете на цял екран', onClick: () => {
    const split = wideBtn.closest('.modal') && wideBtn.closest('.modal').querySelector('.pur-split');
    if (!split) return;
    const on = split.classList.toggle('pur-wide');
    wideBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    wideBtn.textContent = on ? '⇔ Покажи оригинала' : '⇔ Редовете на цял екран';
  } });
  if (d.has_file || (d.attachments || []).length) foot.appendChild(wideBtn);
  if (d.id && d.has_file) foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Оригинал', onClick: () => window.open('/api/purchases/' + d.id + '/file', '_blank', 'noopener') }));
  if (d.status === 'confirmed') foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Разписка', onClick: () => window.open('/api/purchases/' + d.id + '/receipt-pdf', '_blank', 'noopener') }));
  if (d.id && isAdmin() && locked) foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отключи', onClick: () => unlockDialog(d) }));
  if (d.id && isAdmin() && !locked) foot.appendChild(el('button', { class: 'btn ghost danger', type: 'button', text: 'Изтрий', onClick: async () => {
    const ok = await confirmDialog('Изтриване на доставка № ' + (d.number || d.id) + '? Складовите движения се сторнират, а оригиналът остава в „Файлове без документ".', { okText: 'Изтрий', danger: true });
    if (!ok) return;
    try { await api('/purchases/' + d.id, { method: 'DELETE' }); done(null, 'Доставката е изтрита.'); } catch (e) { toast(e.message, true); }
  } }));
  if (!locked) {
    foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Запази', onClick: async () => {
      // Натиснатото „Запази" показва проверката и на празна форма.
      if (!engaged) { engaged = true; recheck(); }
      // Клиентът не потвърждава (Макс, 30.10.2026) — неговата ръчна доставка
      // остава чернова, кантората я потвърждава.
      try { const s = await save(); if (s) done(s, !isNew ? 'Записано.' : (isStaff() ? 'Доставката е записана и потвърдена.' : 'Записано. Кантората ще я прегледа и потвърди.')); } catch (e) { toast(e.message, true); }
    } }));
    // Повторното разчитане — само при оригинал и включен OCR; при грешка
    // бутонът вече стои в лентата горе (два еднакви бутона объркват четеца).
    if (editable && d.has_file && !ocrOff && !ocr.error && !pending(d)) foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Разчети пак', onClick: reread }));
    // „Потвърди" — само кантората (сървърът също отказва на клиента).
    if (d.id && d.status !== 'confirmed' && isStaff()) foot.appendChild(confirmBtn = el('button', { class: 'btn ok', type: 'button', text: 'Потвърди', 'aria-describedby': check.id, onClick: async () => {
      // Синтетичен клик минава и през disabled — затова проверката и тук.
      if (checkTotals(totalsNow()).length) { toast('Сборовете не се сверяват — поправете ги преди потвърждаване.', true); return; }
      // Оставени жълти артикули/описания: човекът не ги е гледал. Потвърждава
      // се след въпрос; такива редове не учат паметта (services/ocr.js).
      const unchecked = lines.filter((l) => Array.isArray(l.review_fields) && (l.review_fields.includes('article') || l.review_fields.includes('description') || l.review_fields.includes('category'))).length;
      if (unchecked && !(await confirmDialog(unchecked + (unchecked === 1 ? ' ред е' : ' реда са') + ' с непрегледан артикул, описание или категория (жълто). Да потвърдя ли документа? Тези редове няма да бъдат запомнени за следващия път.', { okText: 'Потвърди' }))) return;
      // Записът и потвърждаването са две заявки: първо се пази попълненото,
      // после сървърът проверява и заприходява — отказ оставя записаното.
      try {
        const s = await save();
        if (!s) return;
        const c = await api('/purchases/' + d.id + '/confirm', { method: 'POST' });
        done(c, 'Доставката е потвърдена.');
      } catch (e) { toast(e.message, true); }
    } }));
    recheck();
  }

  // Заглавието НАРОЧНО няма думата „фактура": public/registry-autofill.js
  // разпознава фактурната форма по „…актура" в заглавието и би скрил
  // валутата и добавил „Изпрати по имейл" към покупен документ.
  const title = isNew ? 'Ръчна доставка' : 'Доставка' + (d.number ? ' № ' + d.number : '') + ' — ' + ((STATUS[d.status] || [d.status])[0]);
  const ov = openModal({ title, body: el('div', { class: 'pur-split' + (d.has_file || (d.attachments || []).length ? '' : ' no-file') }, left, right), foot, wide: true });
  const modal = ov && ov.querySelector && ov.querySelector('.modal');
  if (modal) modal.classList.add('pur-modal');
  if (!isNew && pending(d) && ov) waitForOcr(ov, d.id);
  return ov;
}

// Докато OCR-ът чете, отвореният документ се пита на всеки 10 сек. Щом е
// готов — прерисува се с редовете. Ако човекът вече е писал нещо в
// прозореца, НЕ се прерисува (би изтрило написаното) — само съобщение.
const DOC_POLL_MS = 10000;
function waitForOcr(ov, id) {
  let typed = false;
  ov.addEventListener('input', () => { typed = true; });
  const tick = async () => {
    if (!ov.isConnected) return;
    let fresh;
    try { fresh = await api('/purchases/' + id); } catch (e) { fresh = null; }
    if (!ov.isConnected) return;
    if (!fresh || pending(fresh)) { setTimeout(tick, DOC_POLL_MS); return; }
    refreshList();
    if (typed) { toast('Разчитането приключи — отвори документа наново, за да видиш редовете.'); return; }
    closeModal();
    await docDialog(fresh);
  };
  setTimeout(tick, DOC_POLL_MS);
}

function refreshList() {
  const page = $('#page');
  // По СОБСТВЕНОТО поле на доставките, не по .drop-zone: зоната е обща с
  // „Заплати" (стъпка 8) и там опресняването би пренаписало чужд екран.
  if (page && page.querySelector('#pur-file-input')) {
    const area = page.lastElementChild;
    if (area) loadList(area);
  }
}

// „Файлове без документ" вече е папка в екрана „Файлове" (ПРОМТ-35) — там са
// и прегледът, „Прикачи към доставка" и кошчето с „Възстанови".
export function orphansDialog() {
  openFiles('orphans');
}

// prefill е или низ (ЕИК-ът от полето за търсене — старото повикване), или
// обект {eik, vat, name} — разчетеният от OCR-а доставчик.
function newSupplier(prefill, onCreated) {
  const p = (prefill && typeof prefill === 'object') ? prefill : { eik: prefill };
  const name = input({ type: 'text', maxlength: '200', value: p.name || '' });
  const eikI = input({ type: 'text', value: p.eik || '', maxlength: '15', inputmode: 'numeric' });
  const vat = input({ type: 'text', maxlength: '20', placeholder: 'BG…', value: p.vat || '' });
  const country = input({ type: 'text', value: 'BG', maxlength: '2' });
  const body = el('div', {}, field('Име на доставчика', name), row(field('ЕИК', eikI, { size: 'm' }), field('ДДС №', vat, { size: 'm' }), field('Държава (2 букви)', country, { size: 's' })));
  const ok = el('button', { class: 'btn', type: 'button', text: 'Създай', onClick: async () => {
    if (!name.value.trim()) { toast('Въведете име.', true); name.focus(); return; }
    try {
      const c = await api('/contacts', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, name: name.value.trim(), eik: eikI.value.trim(), vat_number: vat.value.trim(), country: country.value.trim().toUpperCase() || 'BG', type: 'supplier' }) });
      closeModal();
      toast('Доставчикът е създаден.');
      onCreated(c);
    } catch (e) { toast(e.message, true); }
  } });
  openModal({ title: 'Нов доставчик', body, foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), ok) });
}

// „Нов артикул“ от несъпоставен разчетен ред: попълнен от реда (име,
// мярка, цена). Кодът го дава сървърът (routes/articles.js, nextCode);
// счетоводните полета се допълват после в „Артикули“.
function newArticle(l, onCreated) {
  const co = (state().companies || []).find((c) => c.id === state().activeCompany) || {};
  const name = input({ type: 'text', maxlength: '200', value: l.description || '' });
  const measure = input({ type: 'text', maxlength: '30', value: l.unit || 'бр.' });
  const price = input({ type: 'text', inputmode: 'decimal', value: l.unit_price != null ? commaIn(l.unit_price) : '' });
  const kind = el('select', {}, el('option', { value: 'goods', text: 'Стока' }), el('option', { value: 'material', text: 'Материал' }), el('option', { value: 'service', text: 'Услуга' }));
  kind.value = co.warehouse_mode === 'materials' ? 'material' : 'goods';
  const body = el('div', {}, field('Наименование', name), row(field('Мярка', measure, { size: 's' }), field('Цена', price, { size: 's' }), field('Вид', kind, { size: 'm' })));
  const ok = el('button', { class: 'btn', type: 'button', text: 'Създай', onClick: async () => {
    if (!name.value.trim()) { toast('Въведете наименование.', true); name.focus(); return; }
    try {
      const a = await api('/articles', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, name: name.value.trim(), measure: measure.value.trim() || 'бр.', unit_price: num(price.value), type: kind.value }) });
      closeModal();
      toast('Артикулът е създаден' + (a.code ? ' с код ' + a.code : '') + '.');
      onCreated(a);
    } catch (e) { toast(e.message, true); }
  } });
  openModal({ title: 'Нов артикул', body, foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), ok) });
}

function unlockDialog(d) {
  const reason = el('textarea', { rows: '3', maxlength: '500' });
  const ok = el('button', { class: 'btn', type: 'button', text: 'Отключи', onClick: async () => {
    if (!reason.value.trim()) { toast('Въведете причина.', true); reason.focus(); return; }
    try {
      await api('/purchases/' + d.id + '/unlock', { method: 'POST', body: JSON.stringify({ reason: reason.value.trim() }) });
      closeModal(); closeModal();
      toast('Документът е отключен.');
      refreshList();
    } catch (e) { toast(e.message, true); }
  } });
  openModal({ title: 'Отключване на доставка', body: el('div', {}, el('p', { text: 'Причината влиза в историята на документа.' }), field('Причина', reason)), foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), ok) });
}
